import { useEffect, useMemo, useState } from 'react';
import apiClient from '@lib/api/client';

// ── Carpenter's construction calculator ──────────────────────────────────────
// Modeled on Construction Master / BuildCalc workflows:
//  • mixed-unit keypad (Ft / In / 16ths / 64ths) with exact integer math (1/64" base)
//  • unit-aware chaining: length×length=area, area×length=volume, memory keys, tape
//  • right-angle solver (any two of rise/run/diagonal → the rest + pitch + bevel)
//  • stairs, roof (common/hip/jack), spacing & dividers — each with a LIVE diagram
//  • cut lists saved on-device, shareable by text/email
// Fully offline once loaded — nothing here talks to the network.

const PER_IN = 64;            // base unit: 1/64 inch
const PER_FT = 12 * PER_IN;

type Dim = { ft: string; inch: string; sx: string }; // sx field is SIXTEENTHS (site standard)
const blank = (): Dim => ({ ft: '', inch: '', sx: '' });
const toU = (d: Dim) =>
  (parseFloat(d.ft) || 0) * PER_FT + (parseFloat(d.inch) || 0) * PER_IN + (parseFloat(d.sx) || 0) * 4;

const fmtLen = (u: number): string => {
  const neg = u < 0;
  let t = Math.round(Math.abs(u));
  const ft = Math.floor(t / PER_FT); t -= ft * PER_FT;
  const inch = Math.floor(t / PER_IN); t -= inch * PER_IN;
  let num = t, den = PER_IN;
  while (num % 2 === 0 && num > 0) { num /= 2; den /= 2; }
  const parts: string[] = [];
  if (ft) parts.push(`${ft}'`);
  if (inch || (!ft && !num)) parts.push(`${inch}"`);
  if (num) parts.push(`${num}/${den}"`);
  return (neg ? '−' : '') + (parts.length ? parts.join(' ') : '0"');
};
const inches = (u: number) => u / PER_IN;
const lenDetail = (u: number) =>
  `${(u / PER_FT).toFixed(3)} ft · ${inches(u).toFixed(3)}" · ${(inches(u) * 25.4).toFixed(1)} mm`;

type Val =
  | { kind: 'len'; u: number }
  | { kind: 'area'; sqin: number }
  | { kind: 'vol'; cuin: number }
  | { kind: 'num'; v: number };

const fmtVal = (v: Val): string =>
  v.kind === 'len' ? fmtLen(v.u)
  : v.kind === 'area' ? `${(v.sqin / 144).toFixed(2)} ft²`
  : v.kind === 'vol' ? `${(v.cuin / 1728).toFixed(2)} ft³`
  : String(Math.round(v.v * 10000) / 10000);

const fmtValDetail = (v: Val): string =>
  v.kind === 'len' ? lenDetail(v.u)
  : v.kind === 'area' ? `${(v.sqin * 0.00064516).toFixed(3)} m²`
  : v.kind === 'vol' ? `${(v.cuin / 1728 / 27).toFixed(3)} yd³ · ${(v.cuin * 0.0000163871).toFixed(3)} m³`
  : '';

const applyOp = (a: Val, op: string, b: Val): Val | null => {
  const s = op === '−' ? -1 : 1;
  if (op === '+' || op === '−') {
    if (a.kind === 'len' && b.kind === 'len') return { kind: 'len', u: a.u + s * b.u };
    if (a.kind === 'area' && b.kind === 'area') return { kind: 'area', sqin: a.sqin + s * b.sqin };
    if (a.kind === 'vol' && b.kind === 'vol') return { kind: 'vol', cuin: a.cuin + s * b.cuin };
    if (a.kind === 'num' && b.kind === 'num') return { kind: 'num', v: a.v + s * b.v };
    return null;
  }
  if (op === '×') {
    if (a.kind === 'len' && b.kind === 'len') return { kind: 'area', sqin: inches(a.u) * inches(b.u) };
    if (a.kind === 'area' && b.kind === 'len') return { kind: 'vol', cuin: a.sqin * inches(b.u) };
    if (a.kind === 'len' && b.kind === 'area') return { kind: 'vol', cuin: b.sqin * inches(a.u) };
    if (a.kind === 'len' && b.kind === 'num') return { kind: 'len', u: a.u * b.v };
    if (a.kind === 'num' && b.kind === 'len') return { kind: 'len', u: b.u * a.v };
    if (a.kind === 'area' && b.kind === 'num') return { kind: 'area', sqin: a.sqin * b.v };
    if (a.kind === 'num' && b.kind === 'area') return { kind: 'area', sqin: b.sqin * a.v };
    if (a.kind === 'vol' && b.kind === 'num') return { kind: 'vol', cuin: a.cuin * b.v };
    if (a.kind === 'num' && b.kind === 'num') return { kind: 'num', v: a.v * b.v };
    return null;
  }
  if (op === '÷') {
    if (a.kind === 'len' && b.kind === 'num' && b.v) return { kind: 'len', u: a.u / b.v };
    if (a.kind === 'len' && b.kind === 'len' && b.u) return { kind: 'num', v: a.u / b.u };
    if (a.kind === 'area' && b.kind === 'len' && b.u) return { kind: 'len', u: (a.sqin / inches(b.u)) * PER_IN };
    if (a.kind === 'area' && b.kind === 'num' && b.v) return { kind: 'area', sqin: a.sqin / b.v };
    if (a.kind === 'vol' && b.kind === 'num' && b.v) return { kind: 'vol', cuin: a.cuin / b.v };
    if (a.kind === 'vol' && b.kind === 'area' && b.sqin) return { kind: 'len', u: (a.cuin / b.sqin) * PER_IN };
    if (a.kind === 'num' && b.kind === 'num' && b.v) return { kind: 'num', v: a.v / b.v };
    return null;
  }
  return null;
};

// ── Cut lists (on-device, shareable) ──
interface CutItem { label: string; value: string }
interface CutList { name: string; items: CutItem[] }
const LISTS_KEY = 'calcCutLists';
const loadLists = (): CutList[] => { try { return JSON.parse(localStorage.getItem(LISTS_KEY) || '[]'); } catch { return []; } };
const storeLists = (l: CutList[]) => localStorage.setItem(LISTS_KEY, JSON.stringify(l));

function useCutLists() {
  const [lists, setLists] = useState<CutList[]>(loadLists);
  const save = (l: CutList[]) => { setLists(l); storeLists(l); };
  const addItem = (label: string, value: string) => {
    const names = lists.map(l => l.name);
    const pick = window.prompt(
      names.length
        ? `Save "${value}" to which list?\nExisting: ${names.join(', ')}\n(Type a name — new name makes a new list)`
        : `Name a list to save "${value}" into (e.g. "Smith deck"):`
    );
    if (!pick?.trim()) return false;
    const name = pick.trim();
    const next = [...lists];
    let list = next.find(l => l.name.toLowerCase() === name.toLowerCase());
    if (!list) { list = { name, items: [] }; next.push(list); }
    list.items.push({ label, value });
    save(next);
    return true;
  };
  const addItems = (items: CutItem[], suggestion?: string) => {
    if (!items.length) return false;
    const names = lists.map(l => l.name);
    const pick = window.prompt(
      names.length
        ? `Save ${items.length} pieces to which list?\nExisting: ${names.join(', ')}\n(Type a name — new name makes a new list)`
        : `Name a list to save ${items.length} pieces into:`,
      suggestion || ''
    );
    if (!pick?.trim()) return false;
    const name = pick.trim();
    const next = [...lists];
    let list = next.find(l => l.name.toLowerCase() === name.toLowerCase());
    if (!list) { list = { name, items: [] }; next.push(list); }
    list.items.push(...items);
    save(next);
    return true;
  };
  return { lists, save, addItem, addItems };
}

function SaveBtn({ onSave }: { onSave: () => boolean | void }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { if (onSave() !== false) { setDone(true); setTimeout(() => setDone(false), 1500); } }}
      className="inline-flex items-center gap-1 text-xs font-semibold text-[#C77D3F] border border-[#C77D3F]/30 rounded-lg px-2.5 py-1.5 active:bg-[#C77D3F]/10"
    >
      <span className="material-symbols-outlined text-sm">{done ? 'check' : 'playlist_add'}</span>
      {done ? 'Saved' : 'Save to list'}
    </button>
  );
}

function VolCard({ cuin, onSave }: { cuin: number; onSave?: () => boolean | void }) {
  if (!(cuin > 0)) return null;
  const ft3 = cuin / 1728, yd3 = ft3 / 27, m3 = cuin * 0.0000163871;
  return (
    <div className="bg-gradient-to-br from-[#C77D3F]/15 to-[#C77D3F]/5 border border-[#C77D3F]/30 rounded-xl px-4 py-3 text-center">
      <p className="text-2xl font-bold text-[#C77D3F]">{yd3.toFixed(2)} yd³</p>
      <p className="text-[11px] text-gray-500 mt-0.5">{ft3.toFixed(1)} ft³ · {m3.toFixed(2)} m³{ft3 < 54 ? ` · ≈ ${Math.ceil(ft3 / 0.6)} × 80 lb bags` : ''}</p>
      {onSave && <div className="mt-1.5"><SaveBtn onSave={onSave} /></div>}
    </div>
  );
}

// ── Shared inputs / results ──
function DimInput({ label, value, onChange }: { label: string; value: Dim; onChange: (d: Dim) => void }) {
  const box = 'w-full h-11 px-1 rounded-lg border border-gray-300 text-center text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40';
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {(['ft', 'inch', 'sx'] as const).map((k, i) => (
          <label key={k} className="block">
            <input inputMode="decimal" placeholder="0" value={value[k]}
              onChange={e => onChange({ ...value, [k]: e.target.value })} className={box} />
            <span className="block text-center text-[10px] text-gray-400 mt-0.5">{['feet', 'inches', '16ths'][i]}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Diagrams (live SVG, ochre on cream) ──
const DIAG = { stroke: '#C77D3F', fill: '#C77D3F22', ink: '#7a4a22', grid: '#e8ddd0' };

const HEADER_DEPTH: Record<string, number> = { '2x6': 5.5, '2x8': 7.25, '2x10': 9.25, '2x12': 11.25 };

function TriangleDiagram({ rise, run, labels }: { rise: number; run: number; labels: [string, string, string] }) {
  if (!(rise > 0 && run > 0)) return null;
  const W = 280, H = 150, pad = 26;
  const scale = Math.min((W - 2 * pad) / run, (H - 2 * pad) / rise);
  const rw = run * scale, rh = rise * scale;
  const x0 = (W - rw) / 2, y0 = (H + rh) / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <polygon points={`${x0},${y0} ${x0 + rw},${y0} ${x0 + rw},${y0 - rh}`} fill={DIAG.fill} stroke={DIAG.stroke} strokeWidth="2" strokeLinejoin="round" />
      <rect x={x0 + rw - 12} y={y0 - 12} width="12" height="12" fill="none" stroke={DIAG.stroke} strokeWidth="1.5" />
      <text x={x0 + rw / 2} y={y0 + 16} textAnchor="middle" fontSize="11" fill={DIAG.ink} fontWeight="700">{labels[0]}</text>
      <text x={x0 + rw + 6} y={y0 - rh / 2} fontSize="11" fill={DIAG.ink} fontWeight="700">{labels[1]}</text>
      <text x={x0 + rw / 2 - 14} y={y0 - rh / 2 - 8} textAnchor="end" fontSize="11" fill={DIAG.ink} fontWeight="700" transform={`rotate(${-Math.atan2(rh, rw) * 180 / Math.PI} ${x0 + rw / 2} ${y0 - rh / 2})`}>{labels[2]}</text>
    </svg>
  );
}

// CR #204: dimensioned stair section — every measurement on the drawing:
// per-step rise/run, total rise (left), total run (bottom), stringer length
// along the diagonal, and the stair angle at the base.
function StairsDiagram({ risers, riseU, runU }: { risers: number; riseU: number; runU: number }) {
  if (!(risers > 0 && riseU > 0 && runU > 0)) return null;
  const n = Math.min(risers, 20);
  const W = 300, H = 210, padL = 52, padR = 16, padT = 26, padB = 40;
  const totalRun = runU * (n - 1) || runU;
  const totalRise = riseU * n;
  const scale = Math.min((W - padL - padR) / Math.max(totalRun, 1), (H - padT - padB) / totalRise);
  const x0 = padL, y0 = H - padB;
  const xTop = x0 + totalRun * scale, yTop = y0 - totalRise * scale;
  const stringerU = Math.sqrt(totalRise ** 2 + totalRun ** 2);
  const angleDeg = (Math.atan2(totalRise, totalRun) * 180) / Math.PI;
  let d = `M ${x0} ${y0}`;
  for (let i = 0; i < n; i++) {
    d += ` v ${-riseU * scale}`;
    if (i < n - 1) d += ` h ${runU * scale}`;
  }
  const midX = (x0 + xTop) / 2, midY = (y0 + yTop) / 2;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {/* floor */}
      <line x1={x0 - 8} y1={y0} x2={xTop + 10} y2={y0} stroke={DIAG.grid} strokeWidth="2" />
      {/* steps */}
      <path d={d} fill="none" stroke={DIAG.stroke} strokeWidth="2.5" strokeLinejoin="round" />
      {/* stringer diagonal + label */}
      <line x1={x0} y1={y0} x2={xTop} y2={yTop} stroke={DIAG.ink} strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
      <text x={midX + 8} y={midY - 8} fontSize="9.5" fill={DIAG.ink} fontWeight="700"
        transform={`rotate(${-angleDeg} ${midX + 8} ${midY - 8})`}>
        stringer {fmtLen(stringerU)}
      </text>
      {/* total rise dimension (left) */}
      <line x1={x0 - 26} y1={y0} x2={x0 - 26} y2={yTop} stroke={DIAG.ink} strokeWidth="1" />
      <line x1={x0 - 30} y1={y0} x2={x0 - 22} y2={y0} stroke={DIAG.ink} strokeWidth="1" />
      <line x1={x0 - 30} y1={yTop} x2={x0 - 22} y2={yTop} stroke={DIAG.ink} strokeWidth="1" />
      <text x={x0 - 32} y={(y0 + yTop) / 2} fontSize="9.5" fill={DIAG.ink} fontWeight="700" textAnchor="middle"
        transform={`rotate(-90 ${x0 - 32} ${(y0 + yTop) / 2})`}>
        rise {fmtLen(totalRise)}
      </text>
      {/* total run dimension (bottom) */}
      <line x1={x0} y1={y0 + 16} x2={xTop} y2={y0 + 16} stroke={DIAG.ink} strokeWidth="1" />
      <line x1={x0} y1={y0 + 12} x2={x0} y2={y0 + 20} stroke={DIAG.ink} strokeWidth="1" />
      <line x1={xTop} y1={y0 + 12} x2={xTop} y2={y0 + 20} stroke={DIAG.ink} strokeWidth="1" />
      <text x={(x0 + xTop) / 2} y={y0 + 30} fontSize="9.5" fill={DIAG.ink} fontWeight="700" textAnchor="middle">
        run {fmtLen(totalRun)}
      </text>
      {/* first step: rise + run labels */}
      <text x={x0 + 4} y={y0 - (riseU * scale) / 2 + 3} fontSize="8.5" fill={DIAG.ink}>{fmtLen(riseU)}</text>
      {n > 1 && (
        <text x={x0 + (runU * scale) / 2} y={y0 - riseU * scale - 4} fontSize="8.5" fill={DIAG.ink} textAnchor="middle">{fmtLen(runU)}</text>
      )}
      {/* angle at the base */}
      <path d={`M ${x0 + 30} ${y0} A 30 30 0 0 0 ${x0 + 30 * Math.cos((angleDeg * Math.PI) / 180)} ${y0 - 30 * Math.sin((angleDeg * Math.PI) / 180)}`}
        fill="none" stroke={DIAG.ink} strokeWidth="1" opacity="0.6" />
      <text x={x0 + 38} y={y0 - 8} fontSize="9" fill={DIAG.ink}>{angleDeg.toFixed(1)}°</text>
      {/* header */}
      <text x={W / 2} y={13} textAnchor="middle" fontSize="11" fill={DIAG.ink} fontWeight="700">
        {risers} risers @ {fmtLen(riseU)} · {n - 1 || 1} treads @ {fmtLen(runU)}
      </text>
    </svg>
  );
}

function SpacingDiagram({ total, member, gap, count }: { total: number; member: number; gap: number; count: number }) {
  if (!(total > 0 && count > 0)) return null;
  const W = 280, H = 70, pad = 10;
  const scale = (W - 2 * pad) / total;
  const bars = [];
  let x = gap;
  for (let i = 0; i < Math.min(count, 60); i++) {
    bars.push(<rect key={i} x={pad + x * scale} y={16} width={Math.max(member * scale, 1.5)} height={38} fill={DIAG.stroke} rx="1.5" />);
    x += member + gap;
  }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <rect x={pad} y={12} width={W - 2 * pad} height={46} fill="none" stroke={DIAG.grid} strokeWidth="2" rx="3" />
      {bars}
    </svg>
  );
}

// ── Keypad calculator ──
function KeypadCalc({ addItem }: { addItem: (label: string, value: string) => boolean }) {
  const [eFt, setEFt] = useState<number | null>(null);
  const [eIn, setEIn] = useState<number | null>(null);
  const [eFr, setEFr] = useState<number | null>(null); // in base units (1/64)
  const [typing, setTyping] = useState('');
  const [acc, setAcc] = useState<Val | null>(null);
  const [pendingOp, setPendingOp] = useState<string | null>(null);
  const [tape, setTape] = useState<{ expr: string; result: Val }[]>([]);
  const [mem, setMem] = useState<Val | null>(null);
  const [error, setError] = useState('');

  const entryLabel = () => {
    const bits: string[] = [];
    if (eFt !== null) bits.push(`${eFt}'`);
    if (eIn !== null) bits.push(`${eIn}"`);
    if (eFr !== null) bits.push(fmtLen(eFr));
    if (typing) bits.push(typing);
    return bits.join(' ') || '0';
  };
  const hasEntry = () => eFt !== null || eIn !== null || eFr !== null || typing !== '';
  const clearEntry = () => { setEFt(null); setEIn(null); setEFr(null); setTyping(''); };
  const clearAll = () => { clearEntry(); setAcc(null); setPendingOp(null); setError(''); };

  // "3/8" (tape-measure fraction) or "3.5" or "3" → a number
  const parseTyping = (t: string): number | null => {
    if (!t) return null;
    if (t.includes('/')) {
      const [n, d] = t.split('/');
      const nv = parseFloat(n), dv = parseFloat(d);
      if (!Number.isFinite(nv) || !Number.isFinite(dv) || dv <= 0) return null;
      return nv / dv;
    }
    const v = parseFloat(t);
    return Number.isFinite(v) ? v : null;
  };

  const commitEntry = (): Val | null => {
    if (eFt !== null || eIn !== null || eFr !== null) {
      const extraIn = parseTyping(typing) ?? 0; // trailing fraction reads as inches
      return { kind: 'len', u: (eFt || 0) * PER_FT + ((eIn || 0) + extraIn) * PER_IN + (eFr || 0) };
    }
    if (typing) return { kind: 'num', v: parseTyping(typing) ?? 0 };
    return null;
  };

  const digit = (d: string) => {
    setError('');
    if (d === '.' && (typing.includes('.') || typing.includes('/'))) return;
    // fraction bar: needs digits before it, one per entry, no mixing with decimals
    if (d === '/' && (!typing || typing.includes('/') || typing.includes('.'))) return;
    setTyping(t => (t + d).slice(0, 9));
  };
  const backspace = () => {
    if (typing) setTyping(t => t.slice(0, -1));
    else if (eFr !== null) setEFr(null);
    else if (eIn !== null) setEIn(null);
    else if (eFt !== null) setEFt(null);
  };
  const unit = (u: 'ft' | 'in' | 's16' | 's64') => {
    const n = parseTyping(typing);
    if (n === null) return;
    if (u === 'ft') setEFt(p => (p || 0) + n);
    if (u === 'in') setEIn(p => (p || 0) + n);
    if (u === 's16') setEFr(p => (p || 0) + n * 4);
    if (u === 's64') setEFr(p => (p || 0) + n);
    setTyping('');
  };

  const evaluate = (nextOp: string | null) => {
    const cur = commitEntry();
    setError('');
    if (cur === null) { if (acc !== null) setPendingOp(nextOp); return; }
    let result: Val;
    if (acc !== null && pendingOp) {
      const r = applyOp(acc, pendingOp, cur);
      if (!r) { setError(`Can't ${pendingOp} those units`); return; }
      result = r;
      setTape(t => [{ expr: `${fmtVal(acc)} ${pendingOp} ${fmtVal(cur)}`, result: r }, ...t].slice(0, 40));
    } else result = cur;
    setAcc(result);
    setPendingOp(nextOp);
    clearEntry();
  };

  const memAdd = () => {
    const cur = commitEntry() ?? acc;
    if (!cur) return;
    if (mem && mem.kind === cur.kind) {
      const r = applyOp(mem, '+', cur);
      if (r) setMem(r);
    } else setMem(cur);
  };

  const key = 'h-12 rounded-xl text-lg font-bold active:scale-95 transition-transform select-none';
  const numKey = `${key} bg-white border border-gray-200 text-gray-800 shadow-sm`;
  const opKey = `${key} bg-[#2C1810] text-white`;
  const unitKey = `${key} bg-[#C77D3F]/15 border border-[#C77D3F]/30 text-[#8a5526] text-sm`;
  const memKey = `${key} bg-gray-100 border border-gray-200 text-gray-500 text-xs`;

  return (
    <div className="space-y-3">
      <div className="bg-[#2C1810] rounded-2xl p-4 text-right shadow-inner">
        <div className="flex justify-between items-start">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${mem ? 'bg-[#C77D3F]/30 text-[#C77D3F]' : 'text-transparent'}`}>M {mem ? fmtVal(mem) : ''}</span>
          <p className="text-[#C77D3F]/70 text-xs min-h-[1rem]">{acc !== null ? `${fmtVal(acc)} ${pendingOp || ''}` : ''}</p>
        </div>
        <p className="text-white text-3xl font-bold truncate">{hasEntry() ? entryLabel() : acc !== null ? fmtVal(acc) : '0'}</p>
        <p className="text-white/40 text-[11px] min-h-[1rem]">{error || (!hasEntry() && acc !== null ? fmtValDetail(acc) : '')}</p>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        {['7', '8', '9'].map(d => <button key={d} className={numKey} onClick={() => digit(d)}>{d}</button>)}
        <button className={opKey} onClick={() => evaluate('÷')}>÷</button>
        {['4', '5', '6'].map(d => <button key={d} className={numKey} onClick={() => digit(d)}>{d}</button>)}
        <button className={opKey} onClick={() => evaluate('×')}>×</button>
        {['1', '2', '3'].map(d => <button key={d} className={numKey} onClick={() => digit(d)}>{d}</button>)}
        <button className={opKey} onClick={() => evaluate('−')}>−</button>
        <button className={numKey} onClick={() => digit('0')}>0</button>
        <button className={numKey} onClick={() => digit('.')}>.</button>
        <button className={`${numKey} text-[#C77D3F]`} onClick={() => digit('/')} title="Fraction — e.g. 3 ⁄ 8 then In">⁄</button>
        <button className={opKey} onClick={() => evaluate('+')}>+</button>
        <button className={unitKey} onClick={() => unit('ft')}>Ft</button>
        <button className={unitKey} onClick={() => unit('in')}>In</button>
        <button className={unitKey} onClick={() => unit('s16')}>/16</button>
        <button className={unitKey} onClick={() => unit('s64')}>/64</button>
        <button className={numKey} onClick={backspace}>⌫</button>
        <button className={memKey} onClick={memAdd}>M+</button>
        <button className={memKey} onClick={() => { if (mem) { setAcc(mem); setPendingOp(null); clearEntry(); } }}>MR</button>
        <button className={memKey} onClick={() => setMem(null)}>MC</button>
        <button className={`${key} col-span-4 bg-[#C77D3F] text-white`} onClick={() => evaluate(null)}>=</button>
      </div>

      <div className="flex gap-1.5">
        <button onClick={clearAll} className="flex-1 h-10 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 active:bg-gray-100">Clear</button>
        <button onClick={clearEntry} className="flex-1 h-10 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600 active:bg-gray-100">Clear entry</button>
        {acc !== null && (
          <button onClick={() => addItem('Calc result', fmtVal(acc))}
            className="flex-1 h-10 rounded-xl border border-[#C77D3F]/40 text-sm font-semibold text-[#C77D3F] active:bg-[#C77D3F]/10">
            Save to list
          </button>
        )}
      </div>
      <p className="text-[11px] text-gray-400 text-center">
        Tape-measure entry: <strong>5 Ft · 10 In · 3 ⁄ 8 In</strong> = 5' 10 3/8" · length × length = area
      </p>

      {tape.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-50 max-h-44 overflow-y-auto">
          {tape.map((t, i) => (
            <button key={i} onClick={() => { setAcc(t.result); setPendingOp(null); clearEntry(); }}
              className="w-full text-left px-3 py-2 active:bg-gray-50">
              <p className="text-[11px] text-gray-400">{t.expr} =</p>
              <p className="text-sm font-bold text-gray-900">{fmtVal(t.result)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──
type Tab = 'calc' | 'angle' | 'stairs' | 'roof' | 'sheets' | 'opening' | 'transom' | 'spacing' | 'concrete' | 'materials' | 'convert' | 'lists' | 'planai' | 'orders';

// ── Plan AI + shared orders (portal only — needs the worker's doc access) ──
interface PlanDoc { id: number; name?: string; label?: string | null; file_name?: string; file_path?: string; job_title?: string }
interface AiItem { label: string; qty?: number; stock?: string; length?: string; note?: string }
interface AiCategory { name: string; location?: string; items: AiItem[] }
interface AiResult { categories: AiCategory[]; assumptions?: string[] }
interface SharedList {
  id: number; name: string; category?: string | null; items: CutItem[]; notes?: string | null;
  created_by_name?: string; assigned_to_name?: string | null;
  status: 'open' | 'claimed' | 'fulfilled'; created_at: string;
}
interface Coworker { user_id: number; name: string }

const aiItemsToCut = (cat: AiCategory): CutItem[] =>
  cat.items.map(i => ({
    label: `${i.qty ? `${i.qty}× ` : ''}${i.label}${i.stock ? ` — ${i.stock}` : ''}${i.note ? ` (${i.note})` : ''}`,
    value: i.length || '',
  }));

export const CalculatorPage = () => {
  // Works in both shells: employee portal and admin (/calculator)
  const bcCodeHref = window.location.pathname.startsWith('/portal') ? '/portal/employee/bc-code' : '/bc-code';
  const [tab, setTab] = useState<Tab>('calc');
  const { lists, save: saveLists, addItem, addItems } = useCutLists();
  const inPortal = window.location.pathname.startsWith('/portal');

  // Plan AI
  const [planDocs, setPlanDocs] = useState<PlanDoc[]>([]);
  const [planDocId, setPlanDocId] = useState<number | ''>('');
  const [planFocus, setPlanFocus] = useState('windows_doors');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  // Orders (shared lists)
  const [sharedLists, setSharedLists] = useState<SharedList[]>([]);
  const [coworkers, setCoworkers] = useState<Coworker[]>([]);
  const [shareDraft, setShareDraft] = useState<{ name: string; items: CutItem[]; category?: string } | null>(null);
  const [shareAssignee, setShareAssignee] = useState<number | ''>('');
  const [shareNotes, setShareNotes] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [openOrder, setOpenOrder] = useState<number | null>(null);

  const loadOrders = () =>
    apiClient.get('/portal/me/shared-lists').then(r => setSharedLists(r.data.data ?? [])).catch(() => {});

  useEffect(() => {
    if (!inPortal) return;
    apiClient.get('/portal/me/documents').then(r => {
      const all = (r.data.data ?? []) as PlanDoc[];
      setPlanDocs(all.filter(d => String(d.file_path || d.file_name || '').toLowerCase().endsWith('.pdf')));
    }).catch(() => {});
    apiClient.get('/portal/me/coworkers').then(r => setCoworkers(r.data.data ?? [])).catch(() => {});
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPortal]);

  const runPlanAi = async () => {
    if (!planDocId) return;
    setAiBusy(true); setAiError(''); setAiResult(null);
    try {
      const r = await apiClient.post('/portal/me/analyze-plan', { document_id: planDocId, focus: planFocus }, { timeout: 320000 });
      setAiResult(r.data.data);
    } catch (e) {
      setAiError((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Analysis failed — try again.');
    } finally { setAiBusy(false); }
  };

  const postShared = async () => {
    if (!shareDraft || !shareDraft.name.trim() || shareDraft.items.length === 0) return;
    setShareBusy(true);
    try {
      await apiClient.post('/portal/me/shared-lists', {
        name: shareDraft.name.trim(),
        category: shareDraft.category,
        items: shareDraft.items,
        notes: shareNotes.trim() || undefined,
        assigned_to: shareAssignee || undefined,
      });
      setShareDraft(null); setShareAssignee(''); setShareNotes('');
      loadOrders();
      setTab('orders');
    } finally { setShareBusy(false); }
  };

  const setOrderStatus = async (id: number, status: 'claimed' | 'fulfilled') => {
    setSharedLists(ls => ls.map(l => l.id === id ? { ...l, status } : l));
    try { await apiClient.post(`/portal/me/shared-lists/${id}/status`, { status }); } catch { loadOrders(); }
  };

  // Right-angle solver: any two of rise/run/diag
  const [aRise, setARise] = useState<Dim>(blank());
  const [aRun, setARun] = useState<Dim>(blank());
  const [aDiag, setADiag] = useState<Dim>(blank());
  // Stairs
  const [totalRise, setTotalRise] = useState<Dim>(blank());
  const [maxRiser, setMaxRiser] = useState('7.875');
  // 10-1/16" — BCBC min run is 255 mm; a bare 10" (254 mm) technically fails
  const [tread, setTread] = useState('10.0625');
  const [stairWidth, setStairWidth] = useState('36');
  const [treadStock, setTreadStock] = useState('2x10');
  const [inclRisers, setInclRisers] = useState(true);
  // Roof
  const [roofRun, setRoofRun] = useState<Dim>(blank());
  const [pitch, setPitch] = useState('4');
  const [jackOC, setJackOC] = useState('16');

  // Overhang ladder (#202) — gable-end ladder framing per Erik's spec
  const [ladStock, setLadStock] = useState<'2x4' | '2x6'>('2x4');
  const [ladFly, setLadFly] = useState<Dim>(blank());      // outside plate / fly rafter: long→short of truss next to gable truss
  const [ladInside, setLadInside] = useState<Dim>(blank()); // inside plate: long→square
  const [ladFirst, setLadFirst] = useState<Dim>(blank());   // short point of sub fascia → square-cut drop on rafter chord
  const [ladOverhang, setLadOverhang] = useState<Dim>(blank()); // gable truss face → outside of fly rafter

  // Sheet count (#203) — 4×8 sheathing off top-chord run × sub fascia
  const [shMode, setShMode] = useState<'area' | 'pitch'>('area');
  const [shChord, setShChord] = useState<Dim>(blank());   // run of the top chord (along slope)
  const [shFascia, setShFascia] = useState<Dim>(blank());  // length of gutter sub fascia
  const [shPitchIn, setShPitchIn] = useState('4');         // pitch /12
  const [shTail, setShTail] = useState<Dim>(blank());      // tail → centre, horizontal
  const [shBoth, setShBoth] = useState(false);             // × 2 roof planes
  // Gable overhang ladder
  // Window/Door opening framing
  const [opType, setOpType] = useState<'window' | 'door'>('window');
  const [roW, setRoW] = useState<Dim>(blank());
  const [roH, setRoH] = useState<Dim>(blank());
  const [sillH, setSillH] = useState<Dim>({ ft: '3', inch: '', sx: '' });
  const [wallH, setWallH] = useState<Dim>({ ft: '8', inch: '', sx: '' });
  const [hdr, setHdr] = useState('2x10');
  const [wallStock, setWallStock] = useState<'2x4' | '2x6'>('2x6');
  const [plies, setPlies] = useState<number | 'auto'>('auto');
  const [opOC, setOpOC] = useState('16');
  // Transom opening
  const [trType, setTrType] = useState<'door' | 'window'>('door');
  const [trW, setTrW] = useState<Dim>(blank());
  const [trLowH, setTrLowH] = useState<Dim>({ ft: '6', inch: '10', sx: '' });
  const [trTopH, setTrTopH] = useState<Dim>({ ft: '1', inch: '4', sx: '' });
  const [trSill, setTrSill] = useState<Dim>({ ft: '3', inch: '', sx: '' });
  const [trWallH, setTrWallH] = useState<Dim>({ ft: '9', inch: '', sx: '' });
  const [trHdr, setTrHdr] = useState('2x10');
  const [trStock, setTrStock] = useState<'2x4' | '2x6'>('2x6');
  // Spacing
  const [spTotal, setSpTotal] = useState<Dim>(blank());
  const [spMember, setSpMember] = useState('1.5');
  const [spGap, setSpGap] = useState('4');
  // Concrete
  const [slabL, setSlabL] = useState<Dim>(blank());
  const [slabW, setSlabW] = useState<Dim>(blank());
  const [slabT, setSlabT] = useState('4');
  const [tubeD, setTubeD] = useState('10');
  const [tubeH, setTubeH] = useState<Dim>(blank());
  const [tubeN, setTubeN] = useState('1');
  const [baseD, setBaseD] = useState('6');
  const [ftgLen, setFtgLen] = useState<Dim>(blank());
  const [ftgW, setFtgW] = useState('20');
  const [ftgT, setFtgT] = useState('8');
  const [ftgBars, setFtgBars] = useState('2');
  const [fwLen, setFwLen] = useState<Dim>(blank());
  const [fwH, setFwH] = useState<Dim>({ ft: '8', inch: '', sx: '' });
  const [fwT, setFwT] = useState('8');
  const [fwVOC, setFwVOC] = useState('24');
  const [fwRows, setFwRows] = useState('3');
  const [padL, setPadL] = useState('24');
  const [padW2, setPadW2] = useState('24');
  const [padT, setPadT] = useState('10');
  const [padN, setPadN] = useState('1');
  const [susL, setSusL] = useState<Dim>(blank());
  const [susW, setSusW] = useState<Dim>(blank());
  const [susT, setSusT] = useState('8');
  const [shoreOC, setShoreOC] = useState('48');
  // Materials
  const [wallLen, setWallLen] = useState<Dim>(blank());
  const [oc, setOc] = useState('16');
  const [shL, setShL] = useState<Dim>(blank());
  const [shW, setShW] = useState<Dim>(blank());
  const [waste, setWaste] = useState('10');
  const [bfT, setBfT] = useState('2'); const [bfW, setBfW] = useState('6'); const [bfL, setBfL] = useState('8'); const [bfQ, setBfQ] = useState('1');
  // Convert
  const [c, setC] = useState<Dim>(blank());

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'calc', label: 'Calc', icon: 'calculate' },
    { key: 'angle', label: 'Angle', icon: 'square_foot' },
    { key: 'stairs', label: 'Stairs', icon: 'stairs' },
    { key: 'roof', label: 'Roof', icon: 'roofing' },
    { key: 'sheets', label: 'Sheets', icon: 'grid_on' },
    { key: 'opening', label: 'Opening', icon: 'window' },
    { key: 'transom', label: 'Transom', icon: 'splitscreen' },
    { key: 'spacing', label: 'Spacing', icon: 'view_week' },
    { key: 'concrete', label: 'Concrete', icon: 'foundation' },
    { key: 'materials', label: 'Materials', icon: 'inventory_2' },
    { key: 'convert', label: 'Convert', icon: 'swap_horiz' },
    { key: 'lists', label: 'Lists', icon: 'checklist' },
    ...(inPortal ? [
      { key: 'planai' as Tab, label: 'Plan AI', icon: 'neurology' },
      { key: 'orders' as Tab, label: 'Orders', icon: 'send' },
    ] : []),
  ];

  // ── Right-angle solver: fill in the missing side ──
  const angleSolved = useMemo(() => {
    const r = toU(aRise), n = toU(aRun), d = toU(aDiag);
    let rise = r, run = n, diag = d;
    if (r > 0 && n > 0) diag = Math.sqrt(r * r + n * n);
    else if (r > 0 && d > 0 && d > r) run = Math.sqrt(d * d - r * r);
    else if (n > 0 && d > 0 && d > n) rise = Math.sqrt(d * d - n * n);
    else return null;
    const deg = (Math.atan2(rise, run) * 180) / Math.PI;
    return { rise, run, diag, deg, pitch: (rise / run) * 12 };
  }, [aRise, aRun, aDiag]);

  const uTotalRise = toU(totalRise);
  const maxRiserU = (parseFloat(maxRiser) || 7.875) * PER_IN;
  const riserCount = uTotalRise > 0 ? Math.ceil(uTotalRise / maxRiserU) : 0;
  const riserHeight = riserCount > 0 ? uTotalRise / riserCount : null;
  const treadU = (parseFloat(tread) || 10) * PER_IN;
  const totalRun = riserCount > 1 ? treadU * (riserCount - 1) : null;
  const stringer = riserHeight && totalRun ? Math.sqrt(uTotalRise ** 2 + totalRun ** 2) : null;
  // CR #204 — BCBC 9.8.4.1, private stairs: rise 125–200 mm (4-15/16"–7-7/8"),
  // run 255–355 mm (10-1/16"–14"). Flag anything outside the table.
  const BCBC_RISE_MIN = 4.9375 * PER_IN, BCBC_RISE_MAX = 7.875 * PER_IN;
  const BCBC_RUN_MIN = 10.0625 * PER_IN, BCBC_RUN_MAX = 14 * PER_IN;
  const stairIssues: string[] = [];
  if (riserHeight !== null) {
    if (riserHeight > BCBC_RISE_MAX) stairIssues.push(`Riser ${fmtLen(riserHeight)} exceeds BCBC max 7-7/8" — use ${riserCount + 1} risers (${fmtLen(uTotalRise / (riserCount + 1))} each)`);
    if (riserHeight < BCBC_RISE_MIN) stairIssues.push(`Riser ${fmtLen(riserHeight)} is under BCBC min 4-15/16" — use ${Math.max(1, riserCount - 1)} risers (${fmtLen(uTotalRise / Math.max(1, riserCount - 1))} each)`);
  }
  if (riserCount > 0) {
    if (treadU < BCBC_RUN_MIN) stairIssues.push(`Tread run ${fmtLen(treadU)} is under BCBC min 10-1/16"`);
    if (treadU > BCBC_RUN_MAX) stairIssues.push(`Tread run ${fmtLen(treadU)} exceeds BCBC max 14"`);
  }
  // Stringers for the tread width: treads span ≤16" o.c. between stringers.
  const stairWidthIn = parseFloat(stairWidth) || 0;
  const stringerN = stairWidthIn > 0 ? Math.max(2, Math.floor(stairWidthIn / 16) + 1) : 0;

  const uRoofRun = toU(roofRun);
  const pitchN = parseFloat(pitch) || 0;
  const roofRise = uRoofRun > 0 ? (uRoofRun * pitchN) / 12 : null;
  const commonRafter = uRoofRun > 0 ? uRoofRun * Math.sqrt(1 + (pitchN / 12) ** 2) : null;
  const hipRafter = uRoofRun > 0 ? uRoofRun * Math.sqrt(2 + (pitchN / 12) ** 2) : null;
  const roofDeg = (Math.atan2(pitchN, 12) * 180) / Math.PI;
  const jackOCU = (parseFloat(jackOC) || 16) * PER_IN;
  const jackDiff = jackOCU * Math.sqrt(1 + (pitchN / 12) ** 2);

  // ── Spacing & dividers ──
  const spacing = useMemo(() => {
    const L = toU(spTotal);
    const w = (parseFloat(spMember) || 0) * PER_IN;
    const g = (parseFloat(spGap) || 0) * PER_IN;
    if (!(L > 0) || !(g > 0)) return null;
    if (w > 0) {
      // members with equal gaps each end: n·w + (n+1)·gap = L, gap ≤ target
      const n = Math.ceil((L - g) / (w + g));
      if (n < 1) return null;
      const gap = (L - n * w) / (n + 1);
      if (gap < 0) return null;
      const marks: number[] = [];
      let x = gap;
      for (let i = 0; i < n; i++) { marks.push(x); x += w + g === 0 ? 0 : w + gap; }
      return { n, gap, w, L, marks };
    }
    // pure dividers: n spaces of ≤ target
    const n = Math.ceil(L / g);
    const gap = L / n;
    const marks: number[] = [];
    for (let i = 1; i < n; i++) marks.push(gap * i);
    return { n: n - 1, gap, w: 0, L, marks };
  }, [spTotal, spMember, spGap]);

  const slabCuIn = inches(toU(slabL)) * inches(toU(slabW)) * (parseFloat(slabT) || 0);
  const tubeCuIn = Math.PI * ((parseFloat(tubeD) || 0) / 2) ** 2 * inches(toU(tubeH)) * (parseInt(tubeN) || 1);
  const ftgCuIn = inches(toU(ftgLen)) * (parseFloat(ftgW) || 0) * (parseFloat(ftgT) || 0);
  const fwCuIn = inches(toU(fwLen)) * inches(toU(fwH)) * (parseFloat(fwT) || 0);
  const padCuIn = (parseFloat(padL) || 0) * (parseFloat(padW2) || 0) * (parseFloat(padT) || 0) * (parseInt(padN) || 0);
  const susCuIn = inches(toU(susL)) * inches(toU(susW)) * (parseFloat(susT) || 0);
  const baseCuIn = inches(toU(slabL)) * inches(toU(slabW)) * (parseFloat(baseD) || 0);
  const pourCuIn = ftgCuIn + fwCuIn + slabCuIn + padCuIn + tubeCuIn + susCuIn;
  const ftgRebarFt = (toU(ftgLen) / PER_FT) * (parseFloat(ftgBars) || 0) * 1.1; // 10% laps
  const fwLenFt = toU(fwLen) / PER_FT, fwHFt = toU(fwH) / PER_FT;
  const fwFormsSqFt = 2 * fwLenFt * fwHFt;
  const fwVerts = fwLenFt > 0 ? Math.ceil(inches(toU(fwLen)) / (parseFloat(fwVOC) || 24)) + 1 : 0;
  const fwVertFt = fwVerts * (fwHFt + 2); // +2' dowel into the footing
  const fwHorizFt = (parseFloat(fwRows) || 0) * fwLenFt * 1.1;
  const susSqFt = (toU(susL) / PER_FT) * (toU(susW) / PER_FT);
  const susWtLb = (susCuIn / 1728) * 150; // normal-weight concrete ≈150 lb/ft³
  const susPsf = ((parseFloat(susT) || 0) / 12) * 150;
  const shoreN = susSqFt > 0 ? (Math.ceil(inches(toU(susL)) / (parseFloat(shoreOC) || 48)) + 1) * (Math.ceil(inches(toU(susW)) / (parseFloat(shoreOC) || 48)) + 1) : 0;

  // Overhang ladder (#202): lateral blocks on 24" centres starting from the
  // square cut; the first block sits at the measured offset (short point of
  // sub fascia → square-cut drop on the rafter chord). Blocks land where the
  // ladder spans, so the layout runs the inside plate's long→square length.
  const ladder = useMemo(() => {
    const flyU = toU(ladFly);
    const insideU = toU(ladInside);
    const firstU = toU(ladFirst);
    const overU = toU(ladOverhang);
    if (!(flyU > 0 && insideU > 0)) return null;
    const blockU = overU > 3 * PER_IN ? overU - 3 * PER_IN : 0; // minus two 1-1/2" plates
    const marks: number[] = [];
    if (firstU > 0) {
      for (let p = firstU; p <= insideU; p += 24 * PER_IN) marks.push(p);
    }
    const linealFt = (flyU + insideU + marks.length * blockU) / PER_FT;
    return { flyU, insideU, firstU, blockU, marks, linealFt };
  }, [ladFly, ladInside, ladFirst, ladOverhang]);

  // Sheet count (#203): (top-chord run × gutter sub fascia) ÷ 32 sq ft — or
  // derive the chord run from pitch × horizontal tail→centre first.
  const sheetCalc = useMemo(() => {
    const fasciaFt = toU(shFascia) / PER_FT;
    let chordFt = toU(shChord) / PER_FT;
    let derived = false;
    if (shMode === 'pitch') {
      const p = parseFloat(shPitchIn) || 0;
      const tailFt = toU(shTail) / PER_FT;
      chordFt = tailFt * Math.sqrt(1 + (p / 12) ** 2);
      derived = tailFt > 0;
    }
    if (!(chordFt > 0 && fasciaFt > 0)) return null;
    const planes = shBoth ? 2 : 1;
    const area = chordFt * fasciaFt * planes;
    return { chordFt, area, planes, count: Math.ceil(area / 32), derived };
  }, [shMode, shChord, shFascia, shPitchIn, shTail, shBoth]);

  const uWall = toU(wallLen);
  const ocN = parseFloat(oc) || 16;
  const studCount = uWall > 0 ? Math.floor(inches(uWall) / ocN) + 2 : 0;
  const areaSqFt = (toU(shL) / PER_FT) * (toU(shW) / PER_FT);
  const sheets = areaSqFt > 0 ? Math.ceil((areaSqFt * (1 + (parseFloat(waste) || 0) / 100)) / 32) : 0;
  const boardFeet = ((parseFloat(bfT) || 0) * (parseFloat(bfW) || 0) * (parseFloat(bfL) || 0) / 12) * (parseInt(bfQ) || 1);
  const uC = toU(c);

  const smallBox = 'h-10 px-2 rounded-lg border border-gray-300 text-center text-sm w-20 bg-white';
  const card = 'bg-white rounded-xl border border-gray-200 p-4 space-y-3';

  const shareList = (list: CutList) => {
    const text = `${list.name} — cut list\n` + list.items.map(i => `• ${i.label}: ${i.value}`).join('\n');
    if (navigator.share) navigator.share({ title: list.name, text }).catch(() => {});
    else window.location.href = `mailto:?subject=${encodeURIComponent(list.name + ' — cut list')}&body=${encodeURIComponent(text)}`;
  };

  return (
    <div className="space-y-4 pb-4">
      <div>
        <h1 className="text-xl font-bold text-[#2C1810]">Calculator</h1>
        <p className="text-sm text-gray-500 mt-0.5">Feet · inches · fractions to 1/64 — exact, works offline.</p>
      </div>

      <div className="flex gap-0.5 bg-gray-100 rounded-xl p-1 overflow-x-auto scrollbar-none">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex flex-col items-center gap-0.5 py-1.5 px-2 rounded-lg text-[9px] font-semibold flex-shrink-0 min-w-[3.2rem] ${tab === t.key ? 'bg-white text-[#C77D3F] shadow-sm' : 'text-gray-500'}`}>
            <span className="material-symbols-outlined text-base">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'calc' && <KeypadCalc addItem={addItem} />}

      {tab === 'angle' && (
        <div className={card}>
          <p className="text-xs text-gray-500">Right-angle solver — fill in <strong>any two</strong>, get the rest plus pitch and saw angles.</p>
          <DimInput label="Rise" value={aRise} onChange={setARise} />
          <DimInput label="Run" value={aRun} onChange={setARun} />
          <DimInput label="Diagonal" value={aDiag} onChange={setADiag} />
          {angleSolved && (
            <>
              <TriangleDiagram rise={angleSolved.rise} run={angleSolved.run} labels={[`run ${fmtLen(angleSolved.run)}`, `rise ${fmtLen(angleSolved.rise)}`, `${fmtLen(angleSolved.diag)}`]} />
              <div className="grid grid-cols-3 gap-2 text-center">
                {[['Rise', fmtLen(angleSolved.rise)], ['Run', fmtLen(angleSolved.run)], ['Diagonal', fmtLen(angleSolved.diag)],
                  ['Pitch', `${angleSolved.pitch.toFixed(2)}/12`], ['Angle', `${angleSolved.deg.toFixed(1)}°`], ['Bevel', `${(90 - angleSolved.deg).toFixed(1)}°`]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg py-1.5">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-sm font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
              <div className="text-center"><SaveBtn onSave={() => addItem('Diagonal', fmtLen(angleSolved.diag))} /></div>
            </>
          )}
          <button onClick={() => { setARise(blank()); setARun(blank()); setADiag(blank()); }} className="text-xs text-gray-400">Clear</button>
        </div>
      )}

      {tab === 'stairs' && (
        <div className={card}>
          <DimInput label="Total rise (floor to floor)" value={totalRise} onChange={setTotalRise} />
          <div className="flex gap-3 flex-wrap">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Max riser (in)</span>
              <input inputMode="decimal" value={maxRiser} onChange={e => setMaxRiser(e.target.value)} className={`${smallBox} block mt-1`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Tread run (in)</span>
              <input inputMode="decimal" value={tread} onChange={e => setTread(e.target.value)} className={`${smallBox} block mt-1`} />
            </label>
          </div>
          {riserCount > 0 && riserHeight !== null && (
            <>
              <StairsDiagram risers={riserCount} riseU={riserHeight} runU={treadU} />
              <div className="grid grid-cols-2 gap-2 text-center">
                {[['Risers', `${riserCount} @ ${fmtLen(riserHeight)}`],
                  ['Treads', `${riserCount - 1} @ ${fmtLen(treadU)}`],
                  ['Total run', totalRun ? fmtLen(totalRun) : '—'],
                  ['Stringer length', stringer ? fmtLen(stringer) : '—'],
                  ['Stringers needed', stringerN > 0 ? `${stringerN} for ${stairWidthIn}" width (≤16" o.c.)` : 'enter width below'],
                  ['Stair angle', totalRun ? `${((Math.atan2(uTotalRise, totalRun) * 180) / Math.PI).toFixed(1)}°` : '—']].map(([l, v]) => (
                  <div key={l as string} className="bg-gray-50 rounded-lg py-1.5">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-sm font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
              {/* CR #204 — BCBC 9.8.4 compliance, checked, not just a footnote */}
              {stairIssues.length === 0 ? (
                <p className="text-[11px] font-semibold text-green-700 text-center bg-green-50 border border-green-200 rounded-lg py-1.5">
                  ✓ Within BCBC 9.8.4 (private stairs): rise 4-15/16"–7-7/8", run 10-1/16"–14"
                </p>
              ) : (
                <div className="space-y-1">
                  {stairIssues.map(w => (
                    <p key={w} className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1.5">⚠ {w}</p>
                  ))}
                </div>
              )}
              <div className="text-center"><SaveBtn onSave={() => addItem('Stairs', `${riserCount} risers @ ${fmtLen(riserHeight)}, stringer ${stringer ? fmtLen(stringer) : ''}`)} /></div>

              <div className="border-t border-gray-100 pt-3 space-y-3">
                <h3 className="text-sm font-bold text-gray-900">Parts list</h3>
                <div className="flex gap-3 flex-wrap items-end">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Stair width (in)</span>
                    <input inputMode="decimal" value={stairWidth} onChange={e => setStairWidth(e.target.value)} className={`${smallBox} block mt-1 w-16`} />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Tread stock</span>
                    <div className="flex gap-1 mt-1">
                      {['2x10', '2x12', 'two 2x6'].map(t => (
                        <button key={t} onClick={() => setTreadStock(t)}
                          className={`h-9 px-2.5 rounded-lg text-xs font-semibold border ${treadStock === t ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{t}</button>
                      ))}
                    </div>
                  </label>
                  <label className="flex items-center gap-1.5 pb-2.5 text-xs font-medium text-gray-500">
                    <input type="checkbox" checked={inclRisers} onChange={e => setInclRisers(e.target.checked)} className="accent-[#C77D3F]" />
                    Riser boards
                  </label>
                </div>
                {(() => {
                  const wIn = parseFloat(stairWidth) || 0;
                  if (!(wIn > 0 && stringer)) return null;
                  const strN = Math.max(2, Math.floor(wIn / 16) + 1); // stringers @ ≤16" oc
                  const stockFt = Math.ceil((inches(stringer) + 12) / 24) * 2; // diagonal + ends, next 2' stock
                  const treadN = riserCount - 1;
                  const pieces = [
                    { label: `Stringers — 2x12 (buy ${stockFt}' stock)`, qty: strN, len: stringer },
                    { label: `Treads — ${treadStock}`, qty: treadN * (treadStock === 'two 2x6' ? 2 : 1), len: wIn * PER_IN },
                    ...(inclRisers ? [{ label: 'Riser boards (1x8 / ply rips)', qty: riserCount, len: wIn * PER_IN }] : []),
                  ];
                  return (
                    <>
                      <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                        {pieces.map((pc, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white">
                            <span className="text-xs text-gray-600 flex-1">{pc.label}</span>
                            <span className="text-xs font-bold text-gray-500">{pc.qty} ×</span>
                            <span className="text-sm font-bold text-[#C77D3F]">{fmtLen(pc.len)}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-gray-400">Drop the bottom of each stringer by one tread thickness so the first rise matches. {strN} stringers keeps treads at ≤16" span.</p>
                      <div className="text-center">
                        <SaveBtn onSave={() => addItems(
                          pieces.map(pc => ({ label: `${pc.qty}× ${pc.label}`, value: fmtLen(pc.len) })),
                          `stairs ${riserCount} rise`
                        )} />
                      </div>
                    </>
                  );
                })()}
              </div>
            </>
          )}
          <button onClick={() => setTotalRise(blank())} className="text-xs text-gray-400">Clear</button>
        </div>
      )}

      {tab === 'roof' && (
        <div className="space-y-3">
        <div className={card}>
          <DimInput label="Run (wall to ridge, horizontal)" value={roofRun} onChange={setRoofRun} />
          <div className="flex gap-3 flex-wrap items-end">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Pitch (/12)</span>
              <input inputMode="decimal" value={pitch} onChange={e => setPitch(e.target.value)} className={`${smallBox} block mt-1`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Jack OC (in)</span>
              <input inputMode="decimal" value={jackOC} onChange={e => setJackOC(e.target.value)} className={`${smallBox} block mt-1`} />
            </label>
            <span className="text-sm text-gray-500 pb-2">{roofDeg.toFixed(1)}°</span>
          </div>
          {commonRafter !== null && roofRise !== null && (
            <>
              <TriangleDiagram rise={roofRise} run={uRoofRun} labels={[`run ${fmtLen(uRoofRun)}`, `rise ${fmtLen(roofRise)}`, fmtLen(commonRafter)]} />
              <div className="grid grid-cols-2 gap-2 text-center">
                {[['Common rafter', fmtLen(commonRafter)], ['Hip / valley', fmtLen(hipRafter!)],
                  ['Rise at ridge', fmtLen(roofRise)], ['Jack difference', fmtLen(jackDiff)]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg py-1.5">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-sm font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 text-center">To ridge line — adjust for ridge thickness & overhang. Jack difference = each jack shortens by this along the plate.</p>
              <div className="text-center"><SaveBtn onSave={() => addItem(`Roof ${pitch}/12`, `common ${fmtLen(commonRafter)}, hip ${fmtLen(hipRafter!)}`)} /></div>
            </>
          )}
          <button onClick={() => setRoofRun(blank())} className="text-xs text-gray-400">Clear</button>
        </div>

        <div className={card}>
          <p className="text-xs text-gray-500">
            Gable-end <strong>overhang ladder</strong> — outside plate (fly rafter) is the full
            long→short of the truss next to the gable truss; inside plate is long→square. Lateral
            blocking on 2' centres starting from the square cut.
          </p>
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Build in</span>
            <div className="flex gap-1 mt-1">
              {(['2x4', '2x6'] as const).map(s => (
                <button key={s} onClick={() => setLadStock(s)}
                  className={`h-9 px-3 rounded-lg text-xs font-semibold border ${ladStock === s ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{s}</button>
              ))}
            </div>
          </label>
          <DimInput label="Fly rafter / outside plate (long → short of truss beside gable)" value={ladFly} onChange={setLadFly} />
          <DimInput label="Inside plate (long → square)" value={ladInside} onChange={setLadInside} />
          <DimInput label="First block: short point of sub fascia → square-cut drop" value={ladFirst} onChange={setLadFirst} />
          <DimInput label="Overhang width (gable truss face → outside of fly rafter)" value={ladOverhang} onChange={setLadOverhang} />
          {ladder && (
            <>
              <div className="grid grid-cols-2 gap-2 text-center">
                {[['Lateral blocks', ladder.blockU > 0 ? `${ladder.marks.length} × ${fmtLen(ladder.blockU)}` : `${ladder.marks.length} (enter overhang for length)`],
                  ['Lineal ftg', `${ladder.linealFt.toFixed(1)}'  ${ladStock}`]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg py-1.5">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-sm font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
              {/* Cut list — qty × length, same format as the stairs parts list */}
              <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                {[
                  { label: `Fly rafter / outside plate — ${ladStock} (long → short)`, qty: 1, len: ladder.flyU },
                  { label: `Inside plate — ${ladStock} (long → square)`, qty: 1, len: ladder.insideU },
                  ...(ladder.blockU > 0 ? [{ label: `Lateral blocks — ${ladStock} @ 24" o.c.`, qty: ladder.marks.length, len: ladder.blockU }] : []),
                ].map((pc, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white">
                    <span className="text-xs text-gray-600 flex-1">{pc.label}</span>
                    <span className="text-xs font-bold text-gray-500">{pc.qty} ×</span>
                    <span className="text-sm font-bold text-[#C77D3F]">{fmtLen(pc.len)}</span>
                  </div>
                ))}
              </div>
              {ladder.marks.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[10px] text-gray-500 mb-1">Block layout — from the square cut ({ladStock}, 24" o.c. after the first)</p>
                  <p className="text-xs font-mono text-gray-800 break-words">
                    {ladder.marks.map(m => fmtLen(m)).join(' · ')}
                  </p>
                </div>
              )}
              {ladder.blockU > 0 && (
                <p className="text-[11px] text-gray-400">Block length = overhang − 3" (the two 1-1/2" plates).</p>
              )}
              <div className="text-center">
                <SaveBtn onSave={() => addItems([
                  { label: `Fly rafter ${ladStock}`, value: fmtLen(ladder.flyU) },
                  { label: `Inside plate ${ladStock}`, value: fmtLen(ladder.insideU) },
                  ...(ladder.blockU > 0 ? [{ label: `${ladder.marks.length}× lateral block ${ladStock}`, value: fmtLen(ladder.blockU) }] : []),
                ], 'overhang ladder')} />
              </div>
            </>
          )}
          <button onClick={() => { setLadFly(blank()); setLadInside(blank()); setLadFirst(blank()); setLadOverhang(blank()); }} className="text-xs text-gray-400">Clear</button>
        </div>
        </div>
      )}


      {tab === 'sheets' && (
        <div className={card}>
          <p className="text-xs text-gray-500">4×8 sheathing count — (top-chord run × gutter sub fascia) ÷ 32 sq ft.</p>
          <div className="flex gap-1">
            {([['area', 'Chord run known'], ['pitch', 'From pitch']] as const).map(([m, l]) => (
              <button key={m} onClick={() => setShMode(m)}
                className={`h-9 px-3 rounded-lg text-xs font-semibold border ${shMode === m ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{l}</button>
            ))}
          </div>
          {shMode === 'area' ? (
            <DimInput label="Run of the top chord (along the slope)" value={shChord} onChange={setShChord} />
          ) : (
            <>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Pitch (/12)</span>
                <input inputMode="decimal" value={shPitchIn} onChange={e => setShPitchIn(e.target.value)} className={`${smallBox} block mt-1`} />
              </label>
              <DimInput label="Tail → centre (horizontal)" value={shTail} onChange={setShTail} />
            </>
          )}
          <DimInput label="Length of gutter sub fascia" value={shFascia} onChange={setShFascia} />
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
            <input type="checkbox" checked={shBoth} onChange={e => setShBoth(e.target.checked)} className="accent-[#C77D3F]" />
            Both roof planes (× 2)
          </label>
          {sheetCalc && (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[...(sheetCalc.derived ? [['Chord run', `${sheetCalc.chordFt.toFixed(2)}'`]] : []),
                  ['Area', `${sheetCalc.area.toFixed(1)} ft²${sheetCalc.planes === 2 ? ' (2 sides)' : ''}`],
                  ['4×8 sheets', String(sheetCalc.count)]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg py-1.5">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-sm font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400">Rounded up to whole sheets — no waste factor added.</p>
              <div className="text-center"><SaveBtn onSave={() => addItem('Roof sheathing 4×8', `${sheetCalc.count} sheets (${sheetCalc.area.toFixed(0)} ft²)`)} /></div>
            </>
          )}
          <button onClick={() => { setShChord(blank()); setShTail(blank()); setShFascia(blank()); }} className="text-xs text-gray-400">Clear</button>
        </div>
      )}

      {tab === 'opening' && (() => {
        // BCBC 9.23.12: lintels are built up from at least 2 plies of 38 mm lumber;
        // the wall depth caps how many plies fit (2×4 wall = 2 plies, 2×6 = up to 3).
        const maxPlies = wallStock === '2x6' ? 3 : 2;
        const uW = toU(roW), uH = toU(roH), uSill = opType === 'window' ? toU(sillH) : 0, uWall = toU(wallH);
        // Auto ply: 2-ply to a 4' span, 3-ply past it where the wall depth allows
        const plyCount = plies === 'auto'
          ? Math.min(uW > 4 * PER_FT ? 3 : 2, maxPlies)
          : Math.min(Math.max(plies, 2), maxPlies);
        const plate = 1.5 * PER_IN, dblPlate = 3 * PER_IN;
        const hdrU = (HEADER_DEPTH[hdr] || 9.25) * PER_IN;
        const ocU = (parseFloat(opOC) || 16) * PER_IN;
        if (!(uW > 0 && uH > 0 && uWall > 0)) return (
          <div className={card}>
            <div className="flex gap-1.5">
              {(['window', 'door'] as const).map(t => (
                <button key={t} onClick={() => setOpType(t)}
                  className={`h-9 px-4 rounded-lg text-sm font-semibold border capitalize ${opType === t ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{t}</button>
              ))}
            </div>
            <DimInput label="Rough opening width" value={roW} onChange={setRoW} />
            <DimInput label="Rough opening height" value={roH} onChange={setRoH} />
            {opType === 'window' && <DimInput label="Sill height (floor to RO bottom)" value={sillH} onChange={setSillH} />}
            <DimInput label="Wall height (plate to plate)" value={wallH} onChange={setWallH} />
            <p className="text-xs text-gray-400">Enter the rough opening — the piece list and framing picture appear here.</p>
          </div>
        );
        const sillT = 1.5 * PER_IN;                                  // flat 2x top & bottom sills
        const studLen = uWall - plate - dblPlate;                    // king/common stud
        const openingTop = uSill + uH;                               // above subfloor
        const hdrBot = openingTop + sillT;                           // header bears on the top sill
        const jackLen = hdrBot - plate;                              // jacks carry the header
        const hdrLen = uW + 2 * 1.5 * PER_IN;                        // bears 1-1/2" each side
        const topCripLen = studLen + plate - hdrBot - hdrU;          // header top → underside of top plate
        const botCripLen = uSill - plate - sillT;                    // plate → underside of bottom sill
        const cripCount = Math.max(2, Math.floor(inches(uW) / (parseFloat(opOC) || 16)) + 1);
        void ocU;
        const pieces: { label: string; qty: number; len: number | null }[] = [
          { label: 'King studs', qty: 2, len: studLen },
          { label: 'Jack (trimmer) studs', qty: 2, len: jackLen },
          { label: `Header — ${plyCount}-ply ${hdr}`, qty: plyCount, len: hdrLen },
          { label: 'Top sill / head (flat 2×4)', qty: 1, len: uW },
          ...(topCripLen > PER_IN ? [{ label: 'Top cripples', qty: cripCount, len: topCripLen }] : []),
          ...(opType === 'window' ? [
            { label: 'Bottom sill (flat 2×4)', qty: 1, len: uW },
            ...(botCripLen > PER_IN ? [{ label: 'Bottom cripples', qty: cripCount, len: botCripLen }] : []),
          ] : []),
        ];
        // ── Diagram ──
        const W = 280, H = 190, pad = 14;
        const sc = Math.min((W - 2 * pad) / (uW + 12 * PER_IN), (H - 2 * pad) / uWall);
        const wallW = (uW + 12 * PER_IN) * sc, wallHt = uWall * sc;
        const x0 = (W - wallW) / 2, y0 = H - pad;
        const kx = 3 * PER_IN * sc; // king+jack zone width
        const openX = x0 + kx * 2, openW2 = uW * sc;
        const openBot = y0 - uSill * sc, openTop = y0 - openingTop * sc;
        const hdrTop = openTop - (sillT + hdrU) * sc;
        return (
          <div className={card}>
            <div className="flex gap-1.5">
              {(['window', 'door'] as const).map(t => (
                <button key={t} onClick={() => setOpType(t)}
                  className={`h-9 px-4 rounded-lg text-sm font-semibold border capitalize ${opType === t ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{t}</button>
              ))}
            </div>
            <DimInput label="Rough opening width" value={roW} onChange={setRoW} />
            <DimInput label="Rough opening height" value={roH} onChange={setRoH} />
            {opType === 'window' && <DimInput label="Sill height (floor to RO bottom)" value={sillH} onChange={setSillH} />}
            <DimInput label="Wall height (plate to plate)" value={wallH} onChange={setWallH} />
            <div className="flex gap-3 flex-wrap items-end">
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Wall framing</span>
                <div className="flex gap-1 mt-1">
                  {(['2x4', '2x6'] as const).map(w => (
                    <button key={w} onClick={() => { setWallStock(w); if (w === '2x4' && plies !== 'auto' && plies > 2) setPlies(2); }}
                      className={`h-9 px-2.5 rounded-lg text-xs font-semibold border ${wallStock === w ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{w}</button>
                  ))}
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Plies</span>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => setPlies('auto')}
                    className={`h-9 px-2.5 rounded-lg text-xs font-semibold border ${plies === 'auto' ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>
                    Auto{plies === 'auto' ? ` (${plyCount})` : ''}
                  </button>
                  {[2, 3].map(n => (
                    <button key={n} onClick={() => setPlies(n)} disabled={n > maxPlies}
                      className={`h-9 px-3 rounded-lg text-xs font-semibold border disabled:opacity-30 ${plies === n ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{n}</button>
                  ))}
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Header stock</span>
                <div className="flex gap-1 mt-1">
                  {Object.keys(HEADER_DEPTH).map(h => (
                    <button key={h} onClick={() => setHdr(h)}
                      className={`h-9 px-2.5 rounded-lg text-xs font-semibold border ${hdr === h ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{h}</button>
                  ))}
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Cripple OC (in)</span>
                <input inputMode="decimal" value={opOC} onChange={e => setOpOC(e.target.value)} className={`${smallBox} block mt-1 w-16`} />
              </label>
            </div>

            {/* Framing picture */}
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
              <rect x={x0} y={y0 - wallHt} width={wallW} height={wallHt} fill="none" stroke={DIAG.grid} strokeWidth="2" />
              {/* plates */}
              <rect x={x0} y={y0 - plate * sc} width={wallW} height={plate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={x0} y={y0 - wallHt} width={wallW} height={dblPlate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              {/* kings + jacks */}
              <rect x={openX - kx} y={y0 - wallHt + dblPlate * sc} width={kx / 2} height={wallHt - plate * sc - dblPlate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX + openW2 + kx / 2} y={y0 - wallHt + dblPlate * sc} width={kx / 2} height={wallHt - plate * sc - dblPlate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX - kx / 2} y={openTop - sillT * sc} width={kx / 2} height={(openingTop + 1.5 * PER_IN - plate) * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX + openW2} y={openTop - sillT * sc} width={kx / 2} height={(openingTop + 1.5 * PER_IN - plate) * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              {/* header on the top sill */}
              <rect x={openX - kx / 2} y={hdrTop} width={openW2 + kx} height={hdrU * sc} fill="#C77D3F55" stroke={DIAG.stroke} strokeWidth="1.5" />
              <rect x={openX} y={openTop - sillT * sc} width={openW2} height={sillT * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              {/* opening */}
              <rect x={openX} y={openTop} width={openW2} height={(openingTop - uSill) * sc} fill="white" stroke={DIAG.ink} strokeWidth="1.5" strokeDasharray="5 3" />
              {/* sill + bottom cripples (window) */}
              {opType === 'window' && (
                <>
                  <rect x={openX} y={openBot} width={openW2} height={1.5 * PER_IN * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
                  {Array.from({ length: cripCount }, (_, i) => {
                    const cx = openX + (openW2 / (cripCount - 1 || 1)) * i;
                    return <line key={i} x1={cx} y1={openBot + 1.5 * PER_IN * sc} x2={cx} y2={y0 - plate * sc} stroke={DIAG.stroke} strokeWidth="2" />;
                  })}
                </>
              )}
              {/* top cripples */}
              {topCripLen > PER_IN && Array.from({ length: cripCount }, (_, i) => {
                const cx = openX + (openW2 / (cripCount - 1 || 1)) * i;
                return <line key={`t${i}`} x1={cx} y1={y0 - wallHt + dblPlate * sc} x2={cx} y2={hdrTop} stroke={DIAG.stroke} strokeWidth="2" />;
              })}
            </svg>

            {/* Piece list */}
            <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
              {pieces.map((pc, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white">
                  <span className="text-xs text-gray-600 flex-1">{pc.label}</span>
                  <span className="text-xs font-bold text-gray-500">{pc.qty} ×</span>
                  <span className="text-sm font-bold text-[#C77D3F]">{pc.len !== null ? fmtLen(pc.len) : '—'}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400">Kings run plate-to-plate; jacks stand on the bottom plate under the header (1-1/2" bearing each side). The RO is capped by a flat top sill under the header{opType === 'window' ? ' and a flat bottom sill on the cripples' : ''}. Door plates get cut out after standing the wall.</p>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <span className="material-symbols-outlined text-amber-600 text-base mt-0.5">warning</span>
              <div className="text-[11px] text-amber-800 leading-snug">
                <strong>BCBC 9.23.12:</strong> lintel size AND ply count must satisfy Table 9.23.12.3 for your
                span ({fmtLen(uW)}), snow load, and what the wall carries (roof only vs. roof + floors).
                Auto plies steps 2 → 3 past a 4' span (where wall depth allows) — the table has final say.
                A {plyCount}-ply {hdr} is a cut list, not an approval — check the table:
                <a href={`${bcCodeHref}?q=lintel`} className="block mt-1 font-bold text-[#C77D3F]">
                  → Open BC Code searched for "lintel"
                </a>
              </div>
            </div>
            <div className="text-center">
              <SaveBtn onSave={() => addItems(
                pieces.filter(pc => pc.len !== null).map(pc => ({ label: `${pc.qty}× ${pc.label}`, value: fmtLen(pc.len!) })),
                `${opType} ${fmtLen(uW)} × ${fmtLen(uH)}`
              )} />
            </div>
            <button onClick={() => { setRoW(blank()); setRoH(blank()); }} className="text-xs text-gray-400">Clear</button>
          </div>
        );
      })()}

      {tab === 'transom' && (() => {
        const maxPlies = trStock === '2x6' ? 3 : 2;
        const uW = toU(trW), uLow = toU(trLowH), uTop = toU(trTopH);
        const uSill = trType === 'window' ? toU(trSill) : 0;
        const uWall = toU(trWallH);
        const plate = 1.5 * PER_IN, dblPlate = 3 * PER_IN, bar = 1.5 * PER_IN;
        const hdrU = (HEADER_DEPTH[trHdr] || 9.25) * PER_IN;
        const plyCount = Math.min(uW > 4 * PER_FT ? 3 : 2, maxPlies); // auto, same rule as Opening
        const inputs = (
          <>
            <div className="flex gap-1.5">
              {(['door', 'window'] as const).map(t => (
                <button key={t} onClick={() => setTrType(t)}
                  className={`h-9 px-3 rounded-lg text-sm font-semibold border capitalize ${trType === t ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{t} + transom</button>
              ))}
            </div>
            <DimInput label="Rough opening width" value={trW} onChange={setTrW} />
            <DimInput label={trType === 'door' ? 'Door RO height (below the bar)' : 'Window RO height (below the bar)'} value={trLowH} onChange={setTrLowH} />
            <DimInput label="Transom RO height (above the bar)" value={trTopH} onChange={setTrTopH} />
            {trType === 'window' && <DimInput label="Sill height (floor to RO bottom)" value={trSill} onChange={setTrSill} />}
            <DimInput label="Wall height (plate to plate)" value={trWallH} onChange={setTrWallH} />
            <div className="flex gap-3 flex-wrap items-end">
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Wall framing</span>
                <div className="flex gap-1 mt-1">
                  {(['2x4', '2x6'] as const).map(w => (
                    <button key={w} onClick={() => setTrStock(w)}
                      className={`h-9 px-2.5 rounded-lg text-xs font-semibold border ${trStock === w ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{w}</button>
                  ))}
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Header stock</span>
                <div className="flex gap-1 mt-1">
                  {Object.keys(HEADER_DEPTH).map(h => (
                    <button key={h} onClick={() => setTrHdr(h)}
                      className={`h-9 px-2.5 rounded-lg text-xs font-semibold border ${trHdr === h ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{h}</button>
                  ))}
                </div>
              </label>
            </div>
          </>
        );
        if (!(uW > 0 && uLow > 0 && uTop > 0 && uWall > 0)) return (
          <div className={card}>
            {inputs}
            <p className="text-xs text-gray-400">One header carries the whole opening; a flat bar splits it into the {trType} RO below and the transom RO above.</p>
          </div>
        );
        const sillT = 1.5 * PER_IN; // flat 2x top & bottom sills
        const openingTop = uSill + uLow + bar + uTop;
        const hdrBot = openingTop + sillT; // header bears on the top sill
        const studLen = uWall - plate - dblPlate;
        const jackLen = hdrBot - plate;
        const hdrLen = uW + 3 * PER_IN;
        const topCripLen = studLen + plate - hdrBot - hdrU;
        const botCripLen = uSill - plate - sillT;
        const cripCount = Math.max(2, Math.floor(inches(uW) / 16) + 1);
        const pieces: { label: string; qty: number; len: number }[] = [
          { label: 'King studs', qty: 2, len: studLen },
          { label: 'Jack (trimmer) studs — full height to the header', qty: 2, len: jackLen },
          { label: `Header — ${plyCount}-ply ${trHdr} (auto)`, qty: plyCount, len: hdrLen },
          { label: 'Top sill / head (flat 2×4)', qty: 1, len: uW },
          { label: `Transom bar (flat ${trStock})`, qty: 1, len: uW },
          ...(topCripLen > PER_IN ? [{ label: 'Top cripples', qty: cripCount, len: topCripLen }] : []),
          ...(trType === 'window' ? [
            { label: 'Bottom sill (flat 2×4)', qty: 1, len: uW },
            ...(botCripLen > PER_IN ? [{ label: 'Bottom cripples', qty: cripCount, len: botCripLen }] : []),
          ] : []),
        ];
        const W = 280, H = 200, pad = 14;
        const sc = Math.min((W - 2 * pad) / (uW + 12 * PER_IN), (H - 2 * pad) / uWall);
        const wallW = (uW + 12 * PER_IN) * sc, wallHt = uWall * sc;
        const x0 = (W - wallW) / 2, y0 = H - pad;
        const kx = 3 * PER_IN * sc;
        const openX = x0 + kx * 2, openW2 = uW * sc;
        const yBarBot = y0 - (uSill + uLow) * sc;
        const yTop = y0 - openingTop * sc;
        const hdrTop = yTop - (sillT + hdrU) * sc;
        return (
          <div className={card}>
            {inputs}
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
              <rect x={x0} y={y0 - wallHt} width={wallW} height={wallHt} fill="none" stroke={DIAG.grid} strokeWidth="2" />
              <rect x={x0} y={y0 - plate * sc} width={wallW} height={plate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={x0} y={y0 - wallHt} width={wallW} height={dblPlate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX - kx} y={y0 - wallHt + dblPlate * sc} width={kx / 2} height={wallHt - plate * sc - dblPlate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX + openW2 + kx / 2} y={y0 - wallHt + dblPlate * sc} width={kx / 2} height={wallHt - plate * sc - dblPlate * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX - kx / 2} y={yTop - sillT * sc} width={kx / 2} height={(openingTop + 1.5 * PER_IN - plate) * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX + openW2} y={yTop - sillT * sc} width={kx / 2} height={(openingTop + 1.5 * PER_IN - plate) * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX - kx / 2} y={hdrTop} width={openW2 + kx} height={hdrU * sc} fill="#C77D3F55" stroke={DIAG.stroke} strokeWidth="1.5" />
              <rect x={openX} y={yTop - sillT * sc} width={openW2} height={sillT * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX} y={yTop} width={openW2} height={uTop * sc} fill="white" stroke={DIAG.ink} strokeWidth="1.5" strokeDasharray="5 3" />
              <rect x={openX} y={yBarBot - bar * sc} width={openW2} height={bar * sc} fill={DIAG.fill} stroke={DIAG.stroke} />
              <rect x={openX} y={yBarBot} width={openW2} height={uLow * sc} fill="white" stroke={DIAG.ink} strokeWidth="1.5" strokeDasharray="5 3" />
              {trType === 'window' && <rect x={openX} y={y0 - uSill * sc} width={openW2} height={1.5 * PER_IN * sc} fill={DIAG.fill} stroke={DIAG.stroke} />}
              {topCripLen > PER_IN && Array.from({ length: cripCount }, (_, i) => {
                const cx = openX + (openW2 / (cripCount - 1 || 1)) * i;
                return <line key={`t${i}`} x1={cx} y1={y0 - wallHt + dblPlate * sc} x2={cx} y2={hdrTop} stroke={DIAG.stroke} strokeWidth="2" />;
              })}
              <text x={openX + openW2 / 2} y={yTop + uTop * sc / 2 + 4} textAnchor="middle" fontSize="10" fill={DIAG.ink} fontWeight="700">transom</text>
            </svg>
            <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
              {pieces.map((pc, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white">
                  <span className="text-xs text-gray-600 flex-1">{pc.label}</span>
                  <span className="text-xs font-bold text-gray-500">{pc.qty} ×</span>
                  <span className="text-sm font-bold text-[#C77D3F]">{fmtLen(pc.len)}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400">Jacks run full height — the single header above the transom carries the load, bearing on the flat top sill that caps the RO. The flat bar is not structural: it just splits the RO, nailed through the jacks. Total RO height {fmtLen(openingTop - uSill)} including the 1-1/2" bar.</p>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <span className="material-symbols-outlined text-amber-600 text-base mt-0.5">warning</span>
              <div className="text-[11px] text-amber-800 leading-snug">
                <strong>BCBC 9.23.12:</strong> the header spans the full {fmtLen(uW)} — size and plies must satisfy Table 9.23.12.3. Auto plies is a starting point, not an approval.
                <a href={`${bcCodeHref}?q=lintel`} className="block mt-1 font-bold text-[#C77D3F]">→ Open BC Code searched for "lintel"</a>
              </div>
            </div>
            <div className="text-center">
              <SaveBtn onSave={() => addItems(
                pieces.map(pc => ({ label: `${pc.qty}× ${pc.label}`, value: fmtLen(pc.len) })),
                `${trType}+transom ${fmtLen(uW)}`
              )} />
            </div>
            <button onClick={() => { setTrW(blank()); }} className="text-xs text-gray-400">Clear</button>
          </div>
        );
      })()}

      {tab === 'spacing' && (
        <div className={card}>
          <p className="text-xs text-gray-500">Balusters, pickets, shelves, studs on an odd wall — equal spacing with exact layout marks. Set member width to 0 for plain equal divisions.</p>
          <DimInput label="Total length (inside to inside)" value={spTotal} onChange={setSpTotal} />
          <div className="flex gap-3 flex-wrap">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Member width (in)</span>
              <input inputMode="decimal" value={spMember} onChange={e => setSpMember(e.target.value)} className={`${smallBox} block mt-1`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Max gap (in)</span>
              <input inputMode="decimal" value={spGap} onChange={e => setSpGap(e.target.value)} className={`${smallBox} block mt-1`} />
            </label>
          </div>
          {spacing && (
            <>
              <SpacingDiagram total={spacing.L} member={spacing.w} gap={spacing.gap} count={spacing.w > 0 ? spacing.n : spacing.n + 1} />
              <div className="bg-gradient-to-br from-[#C77D3F]/15 to-[#C77D3F]/5 border border-[#C77D3F]/30 rounded-xl px-4 py-3 text-center">
                <p className="text-xl font-bold text-[#C77D3F]">
                  {spacing.w > 0 ? `${spacing.n} members` : `${spacing.n + 1} spaces`} · gap {fmtLen(spacing.gap)}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">guard rule: gaps must not pass a 4" sphere (BCBC 9.8.8.5)</p>
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-xs font-semibold text-gray-600">Layout marks ({spacing.marks.length}) — from the left edge</summary>
                <div className="mt-2 grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                  {spacing.marks.map((m, i) => (
                    <div key={i} className="flex justify-between bg-gray-50 rounded px-2 py-1 text-xs">
                      <span className="text-gray-400">#{i + 1}</span>
                      <span className="font-bold text-gray-800">{fmtLen(m)}</span>
                    </div>
                  ))}
                </div>
              </details>
              <div className="text-center"><SaveBtn onSave={() => addItem('Spacing', `${spacing.w > 0 ? spacing.n + ' members' : (spacing.n + 1) + ' spaces'} @ gap ${fmtLen(spacing.gap)}`)} /></div>
            </>
          )}
          <button onClick={() => setSpTotal(blank())} className="text-xs text-gray-400">Clear</button>
        </div>
      )}

      {tab === 'concrete' && (
        <div className="space-y-3">
          {pourCuIn > 0 && (
            <div className="bg-[#2C1810] rounded-2xl px-4 py-3 text-center">
              <p className="text-[10px] font-bold text-white/50 uppercase tracking-wide">Total pour — everything below</p>
              <p className="text-2xl font-bold text-[#C77D3F]">{(pourCuIn / 1728 / 27).toFixed(2)} yd³</p>
              <p className="text-[11px] text-white/60">
                {(pourCuIn / 1728).toFixed(1)} ft³ · {(pourCuIn * 0.0000163871).toFixed(2)} m³ · order ≈ {(pourCuIn / 1728 / 27 * 1.08).toFixed(2)} yd³ / {(pourCuIn * 0.0000163871 * 1.08).toFixed(2)} m³ (+8% waste)
              </p>
              <div className="mt-1.5"><SaveBtn onSave={() => addItem('Foundation pour total', `${(pourCuIn / 1728 / 27).toFixed(2)} yd³ (${(pourCuIn * 0.0000163871).toFixed(2)} m³)`)} /></div>
            </div>
          )}

          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Strip footings</h3>
            <DimInput label="Total footing run (all walls added up)" value={ftgLen} onChange={setFtgLen} />
            <div className="flex gap-3 flex-wrap">
              <label className="block"><span className="text-xs font-medium text-gray-500">Width (in)</span>
                <input inputMode="decimal" value={ftgW} onChange={e => setFtgW(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">Thickness (in)</span>
                <input inputMode="decimal" value={ftgT} onChange={e => setFtgT(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">Rebar runs</span>
                <input inputMode="numeric" value={ftgBars} onChange={e => setFtgBars(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
            </div>
            <VolCard cuin={ftgCuIn} onSave={() => addItem('Footing concrete', `${(ftgCuIn / 1728 / 27).toFixed(2)} yd³`)} />
            {ftgRebarFt > 0 && ftgCuIn > 0 && (
              <p className="text-[11px] text-gray-500 text-center">Rebar: {ftgBars} runs ≈ <strong>{Math.ceil(ftgRebarFt)} lin ft</strong> ({(ftgRebarFt * 0.3048).toFixed(0)} m) incl. 10% laps — 15M typical, per your drawings.</p>
            )}
          </div>

          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Foundation walls</h3>
            <DimInput label="Total wall length (perimeter + interior)" value={fwLen} onChange={setFwLen} />
            <DimInput label="Wall height" value={fwH} onChange={setFwH} />
            <div className="flex gap-3 flex-wrap">
              <label className="block"><span className="text-xs font-medium text-gray-500">Thickness (in)</span>
                <input inputMode="decimal" value={fwT} onChange={e => setFwT(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">Vert bar OC (in)</span>
                <input inputMode="decimal" value={fwVOC} onChange={e => setFwVOC(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">Horiz rows</span>
                <input inputMode="numeric" value={fwRows} onChange={e => setFwRows(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
            </div>
            <VolCard cuin={fwCuIn} onSave={() => addItem('Wall concrete', `${(fwCuIn / 1728 / 27).toFixed(2)} yd³`)} />
            {fwCuIn > 0 && (
              <div className="grid grid-cols-2 gap-2 text-center">
                {[['Forming (both faces)', `${Math.ceil(fwFormsSqFt)} ft² · ${(fwFormsSqFt * 0.0929).toFixed(1)} m²`],
                  ['Form ply (4×8)', `≈ ${Math.ceil(fwFormsSqFt / 32)} sheets`],
                  ['Vertical bars', `${fwVerts} pcs @ ${(fwHFt + 2).toFixed(1)}' (incl. 2' dowel)`],
                  ['Rebar total', `${Math.ceil(fwVertFt + fwHorizFt)} lin ft · ${((fwVertFt + fwHorizFt) * 0.3048).toFixed(0)} m`]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg py-1.5 px-1">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-xs font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Slab on grade</h3>
            <DimInput label="Length" value={slabL} onChange={setSlabL} />
            <DimInput label="Width" value={slabW} onChange={setSlabW} />
            <div className="flex gap-3 flex-wrap">
              <label className="block"><span className="text-xs font-medium text-gray-500">Thickness (in)</span>
                <input inputMode="decimal" value={slabT} onChange={e => setSlabT(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">Gravel base (in)</span>
                <input inputMode="decimal" value={baseD} onChange={e => setBaseD(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
            </div>
            <VolCard cuin={slabCuIn} onSave={() => addItem('Slab concrete', `${(slabCuIn / 1728 / 27).toFixed(2)} yd³`)} />
            {slabCuIn > 0 && (
              <div className="grid grid-cols-2 gap-2 text-center">
                {[['Gravel base', `${(baseCuIn / 1728 / 27).toFixed(2)} yd³ · ${(baseCuIn * 0.0000163871).toFixed(2)} m³ · ≈ ${(baseCuIn / 1728 / 27 * 1.4).toFixed(1)} t`],
                  ['Poly / vapour barrier', `${Math.ceil((slabCuIn / (parseFloat(slabT) || 1)) / 144 * 1.1)} ft² · ${(((slabCuIn / (parseFloat(slabT) || 1)) / 144) * 1.1 * 0.0929).toFixed(1)} m² (+10%)`]].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg py-1.5 px-1">
                    <p className="text-[10px] text-gray-500">{l}</p>
                    <p className="text-xs font-bold text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Pad footings</h3>
            <div className="flex gap-2 flex-wrap">
              {([['Length (in)', padL, setPadL], ['Width (in)', padW2, setPadW2], ['Thick (in)', padT, setPadT], ['Qty', padN, setPadN]] as const).map(([l, v, set]) => (
                <label key={l} className="block"><span className="text-xs font-medium text-gray-500">{l}</span>
                  <input inputMode="decimal" value={v} onChange={e => (set as (x: string) => void)(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              ))}
            </div>
            <VolCard cuin={padCuIn} onSave={() => addItem('Pad footings', `${(padCuIn / 1728 / 27).toFixed(2)} yd³`)} />
          </div>

          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Sono tubes / round piers</h3>
            <div className="flex gap-3 flex-wrap">
              <label className="block"><span className="text-xs font-medium text-gray-500">Diameter (in)</span>
                <input inputMode="decimal" value={tubeD} onChange={e => setTubeD(e.target.value)} className={`${smallBox} block mt-1`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">How many</span>
                <input inputMode="numeric" value={tubeN} onChange={e => setTubeN(e.target.value)} className={`${smallBox} block mt-1`} /></label>
            </div>
            <DimInput label="Height / depth" value={tubeH} onChange={setTubeH} />
            <VolCard cuin={tubeCuIn} onSave={() => addItem('Sono tubes', `${(tubeCuIn / 1728 / 27).toFixed(2)} yd³`)} />
          </div>

          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Suspended slab</h3>
            <DimInput label="Length" value={susL} onChange={setSusL} />
            <DimInput label="Width" value={susW} onChange={setSusW} />
            <div className="flex gap-3 flex-wrap">
              <label className="block"><span className="text-xs font-medium text-gray-500">Thickness (in)</span>
                <input inputMode="decimal" value={susT} onChange={e => setSusT(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              <label className="block"><span className="text-xs font-medium text-gray-500">Shore grid OC (in)</span>
                <input inputMode="decimal" value={shoreOC} onChange={e => setShoreOC(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
            </div>
            <VolCard cuin={susCuIn} onSave={() => addItem('Suspended slab', `${(susCuIn / 1728 / 27).toFixed(2)} yd³`)} />
            {susCuIn > 0 && (
              <>
                <div className="grid grid-cols-2 gap-2 text-center">
                  {[['Wet weight', `${Math.round(susWtLb).toLocaleString()} lb · ${(susWtLb * 0.4536 / 1000).toFixed(1)} t`],
                    ['Load on forms', `${Math.round(susPsf)} psf · ${(susPsf * 0.0479).toFixed(1)} kPa`],
                    ['Form ply (4×8)', `≈ ${Math.ceil(susSqFt / 32)} sheets`],
                    ['Shores @ grid', `${shoreN} posts · ≈ ${Math.round(susWtLb / (shoreN || 1)).toLocaleString()} lb (${(susWtLb / (shoreN || 1) * 0.004448).toFixed(1)} kN) each`]].map(([l, v]) => (
                    <div key={l} className="bg-gray-50 rounded-lg py-1.5 px-1">
                      <p className="text-[10px] text-gray-500">{l}</p>
                      <p className="text-xs font-bold text-gray-900">{v}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <span className="material-symbols-outlined text-amber-600 text-base mt-0.5">warning</span>
                  <p className="text-[11px] text-amber-800 leading-snug">
                    <strong>Formwork &amp; shoring for a suspended slab must be designed</strong> (WorkSafeBC OHS Part 20 falsework — engineered drawings, inspection before the pour, reshores per the engineer). Post count here is concrete self-weight only: no live load, no beam concentrations.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'materials' && (
        <div className="space-y-3">
          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Studs in a wall</h3>
            <DimInput label="Wall length" value={wallLen} onChange={setWallLen} />
            <div className="flex gap-1.5">
              {['12', '16', '19.2', '24'].map(o => (
                <button key={o} onClick={() => setOc(o)}
                  className={`h-9 px-3 rounded-lg text-sm font-semibold border ${oc === o ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>{o}"</button>
              ))}
            </div>
            {studCount > 0 && (
              <p className="text-center text-lg font-bold text-[#C77D3F]">{studCount} studs
                <span className="block text-[11px] text-gray-500 font-normal">layout + end · add corners, openings & blocking</span></p>
            )}
          </div>
          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Sheets (4×8) for an area</h3>
            <DimInput label="Length" value={shL} onChange={setShL} />
            <DimInput label="Width / height" value={shW} onChange={setShW} />
            <label className="block"><span className="text-xs font-medium text-gray-500">Waste %</span>
              <input inputMode="decimal" value={waste} onChange={e => setWaste(e.target.value)} className={`${smallBox} block mt-1`} /></label>
            {sheets > 0 && (
              <p className="text-center text-lg font-bold text-[#C77D3F]">{sheets} sheets
                <span className="block text-[11px] text-gray-500 font-normal">{areaSqFt.toFixed(0)} ft² + {waste}% ÷ 32 ft²</span></p>
            )}
          </div>
          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900">Board feet</h3>
            <div className="flex gap-2 flex-wrap items-end">
              {([['Thick (in)', bfT, setBfT], ['Wide (in)', bfW, setBfW], ['Long (ft)', bfL, setBfL], ['Qty', bfQ, setBfQ]] as const).map(([l, v, set]) => (
                <label key={l} className="block"><span className="text-xs font-medium text-gray-500">{l}</span>
                  <input inputMode="decimal" value={v} onChange={e => (set as (s: string) => void)(e.target.value)} className={`${smallBox} block mt-1 w-16`} /></label>
              ))}
            </div>
            {boardFeet > 0 && <p className="text-center text-lg font-bold text-[#C77D3F]">{boardFeet.toFixed(1)} bd ft</p>}
          </div>
        </div>
      )}

      {tab === 'convert' && (
        <div className={card}>
          <DimInput label="Measurement" value={c} onChange={setC} />
          {uC > 0 && (
            <div className="grid grid-cols-2 gap-2 text-center">
              {[['Ft-in-fraction', fmtLen(uC)], ['Decimal feet', `${(uC / PER_FT).toFixed(4)} ft`],
                ['Decimal inches', `${inches(uC).toFixed(4)}"`], ['Millimetres', `${(inches(uC) * 25.4).toFixed(1)} mm`],
                ['Centimetres', `${(inches(uC) * 2.54).toFixed(2)} cm`], ['Metres', `${(inches(uC) * 0.0254).toFixed(4)} m`]].map(([l, v]) => (
                <div key={l} className="bg-gray-50 border border-gray-100 rounded-lg py-2">
                  <p className="text-[10px] text-gray-500">{l}</p>
                  <p className="text-sm font-bold text-gray-900">{v}</p>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => setC(blank())} className="text-xs text-gray-400">Clear</button>
        </div>
      )}

      {tab === 'planai' && (
        <div className="space-y-3">
          <div className={card}>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[#C77D3F]">neurology</span>
              AI plan takeoff
            </h3>
            <p className="text-xs text-gray-500">Pick a builder's plan from your job documents — the AI reads the pages (schedules, dimensions) and builds a categorized dimensional-lumber list organized by where it installs.</p>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Plan (PDF from your jobs)</span>
              <select value={planDocId} onChange={e => setPlanDocId(e.target.value ? Number(e.target.value) : '')}
                className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                <option value="">Select a plan…</option>
                {planDocs.map(d => (
                  <option key={d.id} value={d.id}>{d.label || d.name || d.file_name}{d.job_title ? ` — ${d.job_title}` : ''}</option>
                ))}
              </select>
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {([['windows_doors', 'Windows & doors'], ['stud_packages', 'Stud packages'], ['floor_roof', 'Floor & roof'], ['full', 'Full takeoff']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setPlanFocus(k)}
                  className={`h-9 px-3 rounded-lg text-xs font-semibold border ${planFocus === k ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>
                  {l}
                </button>
              ))}
            </div>
            {aiError && <p className="text-xs text-red-600">{aiError}</p>}
            <button onClick={runPlanAi} disabled={aiBusy || !planDocId}
              className="w-full h-11 rounded-xl bg-[#2C1810] text-white text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2">
              {aiBusy ? (<><span className="material-symbols-outlined animate-spin text-lg">progress_activity</span> Reading the plan — takes a minute or two…</>)
                : (<><span className="material-symbols-outlined text-lg">neurology</span> Build parts list</>)}
            </button>
            <p className="text-[10px] text-gray-400">AI output is a starting point — check counts against the plan before ordering. Header plies etc. still answer to BCBC 9.23.12.</p>
          </div>

          {aiResult && (
            <>
              {(aiResult.assumptions?.length || 0) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <p className="text-[11px] font-bold text-amber-800 mb-0.5">Assumptions made</p>
                  {aiResult.assumptions!.map((a, i) => <p key={i} className="text-[11px] text-amber-800">• {a}</p>)}
                </div>
              )}
              {aiResult.categories.map((cat, ci) => (
                <div key={ci} className={card}>
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="text-sm font-bold text-gray-900">{cat.name}</h3>
                    {cat.location && <span className="text-[10px] text-gray-400 text-right">{cat.location}</span>}
                  </div>
                  <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                    {cat.items.map((it, ii) => (
                      <div key={ii} className="flex items-center gap-2 px-3 py-1.5 bg-white text-xs">
                        <span className="font-bold text-gray-500 w-8 flex-shrink-0">{it.qty ?? ''}×</span>
                        <span className="flex-1 text-gray-800">{it.label}{it.note ? <span className="text-gray-400"> — {it.note}</span> : ''}</span>
                        <span className="font-semibold text-gray-600 flex-shrink-0">{it.stock || ''}</span>
                        <span className="font-bold text-[#C77D3F] flex-shrink-0">{it.length || ''}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <SaveBtn onSave={() => addItems(aiItemsToCut(cat), cat.name)} />
                    <button onClick={() => setShareDraft({ name: cat.name, items: aiItemsToCut(cat), category: 'plan-ai' })}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#C77D3F] rounded-lg px-2.5 py-1.5">
                      <span className="material-symbols-outlined text-sm">send</span> Post as order
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'orders' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Parts lists posted in the app — claim one to fulfill it, or post one of your own lists and assign a coworker.</p>
          {lists.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {lists.map((l, i) => (
                <button key={i} onClick={() => setShareDraft({ name: l.name, items: l.items })}
                  className="h-8 px-2.5 rounded-lg border border-dashed border-[#C77D3F]/40 text-[11px] font-semibold text-[#C77D3F] flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">send</span> Post "{l.name}"
                </button>
              ))}
            </div>
          )}
          {sharedLists.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <span className="material-symbols-outlined text-gray-300 text-4xl">send</span>
              <p className="text-sm text-gray-500 mt-2">No orders posted yet.</p>
            </div>
          ) : sharedLists.map(sl => (
            <div key={sl.id} className={`bg-white rounded-xl border overflow-hidden ${sl.status === 'fulfilled' ? 'border-gray-100 opacity-70' : 'border-gray-200'}`}>
              <button onClick={() => setOpenOrder(o => o === sl.id ? null : sl.id)} className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left">
                <span className={`material-symbols-outlined text-lg ${sl.status === 'fulfilled' ? 'text-green-600' : sl.status === 'claimed' ? 'text-blue-500' : 'text-amber-500'}`}>
                  {sl.status === 'fulfilled' ? 'check_circle' : sl.status === 'claimed' ? 'assignment_ind' : 'assignment'}
                </span>
                <span className="flex-1 min-w-0">
                  <span className={`block text-sm font-semibold truncate ${sl.status === 'fulfilled' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{sl.name}</span>
                  <span className="block text-[10px] text-gray-400 truncate">
                    {sl.items.length} items · {sl.created_by_name}{sl.assigned_to_name ? ` → ${sl.assigned_to_name}` : ' → anyone'}
                  </span>
                </span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${sl.status === 'fulfilled' ? 'bg-green-100 text-green-700' : sl.status === 'claimed' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-800'}`}>{sl.status}</span>
              </button>
              {openOrder === sl.id && (
                <div className="border-t border-gray-100 px-3.5 py-2 space-y-1">
                  {sl.notes && <p className="text-[11px] text-gray-500 italic">"{sl.notes}"</p>}
                  {sl.items.map((it, ii) => (
                    <div key={ii} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-gray-700">{it.label}</span>
                      <span className="font-bold text-gray-900 whitespace-nowrap">{it.value}</span>
                    </div>
                  ))}
                  <div className="flex gap-1.5 pt-1.5">
                    {sl.status === 'open' && (
                      <button onClick={() => setOrderStatus(sl.id, 'claimed')}
                        className="h-8 px-3 rounded-lg bg-blue-600 text-white text-[11px] font-bold">I'll fulfill this</button>
                    )}
                    {sl.status !== 'fulfilled' && (
                      <button onClick={() => setOrderStatus(sl.id, 'fulfilled')}
                        className="h-8 px-3 rounded-lg bg-green-600 text-white text-[11px] font-bold">Mark fulfilled</button>
                    )}
                    <button onClick={() => addItems(sl.items, sl.name)}
                      className="h-8 px-3 rounded-lg border border-gray-200 text-[11px] font-semibold text-gray-600">Copy to my lists</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Post-an-order modal */}
      {shareDraft && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShareDraft(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[#C77D3F]">send</span>
              Post parts list in the app
            </h3>
            <input value={shareDraft.name} onChange={e => setShareDraft(d => d ? { ...d, name: e.target.value } : d)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none" />
            <p className="text-[11px] text-gray-400">{shareDraft.items.length} item{shareDraft.items.length !== 1 ? 's' : ''}</p>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Assign to</span>
              <select value={shareAssignee} onChange={e => setShareAssignee(e.target.value ? Number(e.target.value) : '')}
                className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none">
                <option value="">Anyone (open order)</option>
                {coworkers.map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
            </label>
            <textarea value={shareNotes} onChange={e => setShareNotes(e.target.value)} rows={2}
              placeholder="Note — e.g. 'Pull for Friday, lumber yard order by Wed'"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />
            <div className="flex gap-2">
              <button onClick={() => setShareDraft(null)} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={postShared} disabled={shareBusy || !shareDraft.name.trim()}
                className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
                {shareBusy ? 'Posting…' : 'Post it'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'lists' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Cut lists live on this device. Use "Save to list" on any result, then share a list to the crew by text or email.</p>
          {lists.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <span className="material-symbols-outlined text-gray-300 text-4xl">checklist</span>
              <p className="text-sm text-gray-500 mt-2">No lists yet — save a result from any tab.</p>
            </div>
          ) : lists.map((list, li) => (
            <div key={li} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
                <span className="material-symbols-outlined text-[#C77D3F] text-lg">folder</span>
                <span className="flex-1 font-bold text-sm text-gray-900">{list.name}</span>
                {inPortal && (
                  <button onClick={() => { setShareDraft({ name: list.name, items: list.items }); setTab('orders'); }}
                    title="Post in the app — assign a coworker to fulfill it"
                    className="h-8 w-8 rounded-lg text-[#C77D3F] active:bg-[#C77D3F]/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-lg">send</span>
                  </button>
                )}
                <button onClick={() => shareList(list)} title="Share by text/email"
                  className="h-8 w-8 rounded-lg text-gray-500 active:bg-gray-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-lg">share</span>
                </button>
                <button onClick={() => { if (window.confirm(`Delete list "${list.name}"?`)) saveLists(lists.filter((_, i) => i !== li)); }}
                  className="h-8 w-8 rounded-lg text-gray-400 active:bg-gray-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-lg">delete</span>
                </button>
              </div>
              {list.items.length === 0 ? (
                <p className="px-4 py-3 text-xs text-gray-400">Empty</p>
              ) : list.items.map((it, ii) => (
                <div key={ii} className="flex items-center gap-2 px-4 py-2 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-500 flex-1">{it.label}</span>
                  <span className="text-sm font-bold text-gray-900">{it.value}</span>
                  <button onClick={() => {
                    const next = [...lists];
                    next[li] = { ...list, items: list.items.filter((_, x) => x !== ii) };
                    saveLists(next);
                  }} className="text-gray-300 active:text-red-500">
                    <span className="material-symbols-outlined text-base">close</span>
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
